import SalesExtrasPicker from "../components/pos/SalesExtrasPicker";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { api, API, idr, pesanGagalDariServer } from "../lib/api";
import {
  Plus, Trash2, X, Download, Phone, MessageCircle, Send, CreditCard,
  Repeat, Split, Pencil, Clock, Lock, History, FileText, Info, Upload,
  Sparkles, Copy, ExternalLink, Printer, Undo2, AlertTriangle,
} from "lucide-react";
import { toast } from "sonner";
import { useAuth } from "../context/AuthContext";
import { bolehPakaiAI } from "../lib/izinAI";
import { hitungNominalReturDefault, susunReturDariNota } from "../lib/retur";
import InvoicePreview from "../components/faktur/InvoicePreview";
import PemilihTemplate from "../components/faktur/PemilihTemplate";
import ItemCariField from "../components/pos/ItemCariField";
import PilihPelangganKartu, { kartuCocok } from "../components/pos/PilihPelangganKartu";
import InputRupiah from "../components/InputRupiah";
import { useCabangOptions } from "../lib/cabang";
import { useGudangOptions, wajibPilihGudang, pesanGudangButaArah } from "../lib/gudang";
import { DatePicker, MonthPicker } from "@/components/ui/date-picker";
import LoadingState from "@/components/ui/loading-state";
import { hariIniISO, bulanIniISO, formatTanggal } from "@/lib/tanggal";
import {
  TabelDesktop, DaftarKartuMobile, KartuBaris, KartuBarisHeader, KartuBarisMeta,
} from "@/components/ui/responsive-table";
import SourceInfoField, { useSourceInfoConfig, validasiSourceInfo } from "../components/SourceInfoField";
import {
  PaymentMethodSelect, MetodePembayaranManager, TombolKelolaMetode, bolehKelolaMetode,
} from "../components/pembayaran/MetodePembayaran";
import { useModalGuard, useModalLayer, UnsavedGuardBar } from "../hooks/useModalGuard";
import { useSortState, sortRows, SortTh } from "@/components/ui/sort";
import {
  KATEGORI_ITEM, KATEGORI_DEFAULT, labelKategori, normalKategori, deriveKode, deriveKategoriTeksBebas,
} from "@/lib/kategoriBarang";
import BundleKomponenPicker, {
  useResepBundlingAktif, useProdukInfoCache, bangunKomponenKosong,
  cariAlasanBundleBelumSiap, kumpulkanSemuaImeiForm,
} from "@/components/bundling/BundleKomponenPicker";
import {
  TI_STATUS, TI_KELENGKAPAN, jumlahDiskonBaris, cekDiskonBaris, metodeTradeIn, angka,
  labelTempo, buangMedanLayar, patchBarisDenganKunciPaket, hargaPaketTerkunci,
  diskonNotaTersimpan, diskonKirimEditRinci, diskonPratinjauEditRinci,
} from "@/lib/notaPenjualan";

const nowTime = () => {
  const d = new Date();
  return `${String(d.getHours()).padStart(2, "0")}:${String(d.getMinutes()).padStart(2, "0")}`;
};

const emptyForm = () => ({
  id: null,
  tanggal: hariIniISO(),
  waktu: nowTime(),
  nama_pelanggan: "", no_hp: "",
  // `diskon` = diskon PRODUK per baris (brief 22 Sep D5/D6 — aturan yang sama
  // dengan Input Nota: diskon nota = jumlah diskon baris).
  items: [{ nama_barang: "", harga: 0, diskon: 0, kode: "HP", kategori: KATEGORI_DEFAULT, sku: "", imei: "", is_bonus: false, non_stok: false, komponen: null }],
  diskon_pic_approval: "",
  // Penjualan hasil Input Nota FORMAT BARU (D4): baris `payments` = satu-satunya
  // sumber uang. Diisi dari dokumen saat edit; form Input Penjualan baru tetap
  // format lama (layarnya belum dipindah).
  pembayaran_rinci: false,
  imei: "", metode_pembayaran: "Cash", trade_in: false, trade_in_note: "",
  trade_in_items: [],
  // Satu nota = satu gudang (kontrak G1 keputusan owner #4) — "" berarti
  // biarkan backend menurunkan sendiri (lihat useGudangOptions). Hanya
  // relevan dipilih manual kalau `tampilkanPilihGudang` true.
  warehouse_id: "",
  harga_terima: 0, catatan: "", nama_penjual: "", branch: "",
  // Diskon & biaya admin (brief bos) — "" mode berarti tanpa biaya admin sama
  // sekali (dikirim sebagai null ke backend, lihat `save()`).
  diskon_nominal: 0, biaya_admin_mode: "", biaya_admin_nilai: 0,
  // PPN (keputusan owner 18 Agu 2026) — toggle tingkat DOKUMEN. Tarif &
  // nominal sesungguhnya DIHITUNG SERVER (routers/sales.py) — angka yang
  // dipratinjaukan di sini murni untuk kasir, bukan sumber kebenaran.
  kena_ppn: false,
  is_split: false,
  payments: [{ method: "Cash", amount: 0 }],
  // Sumber Info / atribusi marketing (brief bos 11 Agu 2026, WAJIB selama
  // saklar darurat aktif — lihat components/SourceInfoField.jsx). "" (bukan
  // null) supaya <select> terkontrol dari awal render.
  source_info: "", source_ref_id: "",
});

// Pratinjau SAJA — lihat komentar TARIF_PPN_DEFAULT di backend
// (routers/finance/_shared.py): tarif yang benar-benar tersimpan datang dari
// respons server, tidak pernah dari konstanta ini.
const PPN_PERSEN_PREVIEW = 11;

// Format 'YYYY-MM-DD HH:MM' → 'DD Mmm HH:MM'
const fmtDateTime = (d, t) => {
  if (!d) return "-";
  const dt = new Date(d);
  const m = ["Jan","Feb","Mar","Apr","Mei","Jun","Jul","Agu","Sep","Okt","Nov","Des"];
  const s = `${dt.getDate()} ${m[dt.getMonth()]}`;
  return t ? `${s} · ${t}` : s;
};

// Compute editable status client-side using created_at + window
const canEditNow = (createdAt, windowMin) => {
  if (!createdAt) return false;
  try {
    const t = new Date(createdAt).getTime();
    return (Date.now() - t) / 60000 <= (windowMin || 60);
  } catch { return false; }
};
const minutesLeft = (createdAt, windowMin) => {
  if (!createdAt) return 0;
  try {
    const t = new Date(createdAt).getTime();
    return Math.max(0, Math.ceil((windowMin || 60) - (Date.now() - t) / 60000));
  } catch { return 0; }
};

export default function Sales() {
  const { user, can } = useAuth();
  const BRANCHES = useCabangOptions({ denganKosong: true });
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const canEdit = user?.role !== "viewer";
  // Autocomplete produk memakai modul inventory_produk — modul berbeda dari
  // sales. Kalau tidak diizinkan, kolom nama barang tetap ketik bebas (tidak
  // pernah memanggil endpoint lookup, jadi tidak ada 403 yang mengganggu).
  const bolehLookupProduk = can("inventory_produk", "view");
  // Pemilih IMEI per baris barang (brief bos poin 4: IMEI hanya boleh
  // DIPILIH dari unit yang terdaftar di inventory, bukan diketik bebas).
  // Modul beda lagi dari inventory_produk — kalau tidak diizinkan, baris
  // ber-SKU tetap bisa disimpan TANPA IMEI (backend tidak pernah memaksa
  // IMEI terisi), pemilihnya sekadar tidak muncul.
  const bolehLookupUnit = can("inventory_unit", "view");
  // Pemilih gudang nota (kontrak G1 keputusan owner #4: satu nota = satu
  // gudang) — hanya relevan/tampil saat kasir memang berhak atas LEBIH dari
  // satu gudang (lihat docstring useGudangOptions). Berhak 0/1 gudang -> tidak
  // pernah tampil, backend menurunkan gudangnya sendiri.
  const bolehLihatGudang = can("inventory_gudang", "view");
  const warehouses = useGudangOptions(bolehLihatGudang);
  // Wajib pilih HANYA kalau backend sungguh akan menolak tanpanya — lihat
  // docstring `wajibPilihGudang` (lib/gudang.js). Bukan sekadar
  // `warehouses.length > 1`: admin/superadmin/finance_ceo melihat SEMUA
  // gudang aktif lewat endpoint yang sama, jadi length>1 nyaris selalu true
  // untuk mereka walau backend tidak pernah memaksa mereka memilih.
  const tampilkanPilihGudang = wajibPilihGudang(user, warehouses);
  // Cetak faktur/struk (brief #17-19) — template desain sendiri masih dijaga
  // modul "faktur" (Admin), sementara cetak transaksinya mengikuti izin sales
  // yang sudah dipegang siapa pun yang bisa membuka detail nota ini.
  const bolehFaktur = can("faktur", "view");
  // Sumber Info (brief bos 11 Agu 2026) — saklar darurat + pilihan + daftar
  // KOL/PFP aktif diambil SEKALI di sini (bukan tiap modal dibuka/tiap
  // ketikan), lihat docstring useSourceInfoConfig.
  const sourceInfoCfg = useSourceInfoConfig();
  const [items, setItems] = useState([]);
  const [employees, setEmployees] = useState([]);
  const [periode, setPeriode] = useState(bulanIniISO());
  const [dateFrom, setDateFrom] = useState("");
  const [dateTo, setDateTo] = useState("");
  const [branch, setBranch] = useState("");
  const [modal, setModal] = useState(false);
  const [modalMode, setModalMode] = useState("create"); // create | edit
  const [form, setForm] = useState(emptyForm());
  // B2 (brief 22 Sep): nota B2B/USER yang diedit wajib memakai nama kartu
  // pelanggan. Form "Input Penjualan" baru tidak pernah mengirim jenis
  // pelanggan (selalu umum), jadi hanya jalur edit yang terkena.
  const perluKartuPelanggan = modalMode === "edit" && ["b2b", "user"].includes(form.jenis_pelanggan);
  // Paket/bundling (poin 8, brief bos 23 Agu 2026) — resep AKTIF (dengan stok
  // virtualnya) dimuat ULANG tiap modal dibuka supaya angka stok paket yang
  // dilihat kasir selalu segar (bukan cache basi dari kunjungan sebelumnya).
  // Tanpa `bolehLookupProduk`, resepMap tetap kosong -> baris tetap berjalan
  // seperti barang biasa, sama seperti ItemNamaField kalau lookup produk
  // tidak diizinkan (lihat docstring BundleKomponenPicker.jsx).
  const { resepMap: resepBundlingMap } = useResepBundlingAktif(bolehLookupProduk && modal);
  const { produkInfoCache, ensureProdukInfo } = useProdukInfoCache(bolehLookupProduk);
  // Dialog konfirmasi PPN sebelum simpan (brief bos) — menyimpan payload yang
  // sudah divalidasi, menunggu klik "Lanjutkan". null = tidak tampil.
  const [ppnConfirm, setPpnConfirm] = useState(null);
  const [detailId, setDetailId] = useState(null);
  const [detail, setDetail] = useState(null);
  const [editWindow, setEditWindow] = useState(60);
  const [saving, setSaving] = useState(false);
  const [draftModal, setDraftModal] = useState(null); // {loading, message, wa_url, no_hp, nama_pelanggan, sale_id}
  // {jenis, step, templates, templateId, loading, data} — step: "memuat_template" |
  // "pilih_template" | "kosong" | "tanpa_izin" | "preview". Lihat bukaCetak()/
  // muatDokumenCetak() di bawah.
  const [cetakModal, setCetakModal] = useState(null);
  // PIN persetujuan hapus (brief #23) — satu dialog dipakai ulang untuk
  // penghapusan nota manapun di halaman ini via `pinDialog.retry(pin)`.
  const [pinDialog, setPinDialog] = useState(null); // {pin, submitting, error, retry}
  // Ajukan retur penjualan (brief bos) — dibuka dari panel detail transaksi;
  // approval sesungguhnya terjadi di halaman /retur (agen lain).
  const [returModal, setReturModal] = useState(null);
  const [kasBankAccounts, setKasBankAccounts] = useState([]);
  // Metode pembayaran (samakan dengan Input Nota, brief bos) — sumber TUNGGAL
  // koleksi `payment_methods` lewat `/payment-methods`, BUKAN lagi daftar
  // ringkas ("Transfer"/"Debit"/dst.) yang ditanam mati di berkas ini. Tanpa
  // ini, penjualan yang diinput langsung di sini (bukan lewat approval Nota)
  // kehilangan info bank ("Transfer BCA" runtuh jadi "Transfer") dan tidak
  // bisa direkonsiliasi per bank di Rekon Sales Harian — lihat
  // `_rekon_channel_dari_metode` (server.py), yang kini memakai nilai metode
  // APA ADANYA sebagai channel.
  const [paymentMethods, setPaymentMethods] = useState([]);   // active-only, untuk dropdown
  const [paymentGroups, setPaymentGroups] = useState({});     // active-only, dikelompokkan (sama shape dgn InputNota.jsx)
  // Layar kelola metode. Halaman INI sebelumnya tidak punya tombolnya sama
  // sekali padahal justru di sini kasir paling sering bertemu metode baru —
  // kalau metodenya belum terdaftar/terpetakan, uangnya jatuh ke Kas 1010.
  const [paymentGroupsAll, setPaymentGroupsAll] = useState({});
  const [methodsModal, setMethodsModal] = useState(false);
  // Jebakan harga_terima efektif (edit nota berdiskon) — openEdit memuat
  // form.harga_terima dari sale.harga_terima, yang untuk nota berdiskon/biaya
  // admin BUKAN ketikan kasir, melainkan nilai EFEKTIF hasil hitung server
  // (subtotal - diskon + admin) saat nota itu masih pakai diskon/admin. Flag
  // ini menandai kondisi tsb supaya save() tahu kapan nilai itu harus
  // diabaikan (lihat pemakaian di `hargaTerimaJebakan` & payload di save()).
  const [hargaTerimaOverride, setHargaTerimaOverride] = useState({ aktif: false, nilaiAwal: 0 });
  // Deep-link ke satu transaksi (brief bos 12 Agu #7) — Pelanggan.jsx menautkan
  // baris riwayat belanja ke `/sales?id=<sale_id>`. Pesan ini menjelaskan KENAPA
  // transaksi tertaut gagal dibuka (dihapus / tak berhak / error lain) supaya
  // pengguna tidak mendarat di halaman kosong dan mengira datanya hilang.
  const [linkedSaleNotice, setLinkedSaleNotice] = useState(null);

  const load = useCallback(() => {
    const p = new URLSearchParams();
    if (dateFrom || dateTo) {
      if (dateFrom) p.set("date_from", dateFrom);
      if (dateTo) p.set("date_to", dateTo);
    } else if (periode) {
      p.set("periode", periode);
    }
    if (branch) p.set("branch", branch);
    api.get(`/sales?${p}`).then(r => setItems(r.data));
  }, [periode, dateFrom, dateTo, branch]);

  useEffect(() => { load(); }, [load]);

  // Rekap trade-in ikut filter yang sama; hanya refetch saat panelnya terbuka.
  const [tradeInData, setTradeInData] = useState(null);
  const [showTradeIn, setShowTradeIn] = useState(false);
  const [loadingTradeIn, setLoadingTradeIn] = useState(false);
  // Pengurutan tabel rekap trade-in (client-side, lihat components/ui/sort.jsx) —
  // urutan bawaan tetap seperti sebelumnya (mengikuti urutan `rows` dari server,
  // yang sudah `.sort("tanggal", -1)`), header cuma jadi opsional untuk pengguna.
  const tradeInSort = useSortState(null, "asc");
  useEffect(() => {
    if (!showTradeIn) return;
    loadTradeIn();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [periode, dateFrom, dateTo, branch, showTradeIn]);
  useEffect(() => {
    api.get("/employees").then(r => {
      const list = (r.data || []).filter(e => e.is_active !== false)
        .sort((a, b) => a.nama.localeCompare(b.nama));
      setEmployees(list);
    }).catch(() => setEmployees([]));
    api.get("/config").then(r => setEditWindow(r.data?.edit_window_minutes || 60)).catch(() => {});
    loadMethods();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Sama persis endpoint & shape yang dipakai InputNota.jsx (active_only=true
  // untuk dropdown form, active_only=false untuk layar kelola) — satu sumber
  // data, bukan cara kedua.
  const loadMethods = () => {
    api.get("/payment-methods?active_only=true").then(r => {
      setPaymentMethods(r.data?.items || []);
      setPaymentGroups(r.data?.groups || {});
    }).catch(() => {});
    api.get("/payment-methods?active_only=false").then(r => {
      setPaymentGroupsAll(r.data?.groups || {});
    }).catch(() => {});
  };

  const totalItems = useMemo(
    () => form.items.reduce((a, x) => a + (parseFloat(x.harga) || 0), 0),
    [form.items]
  );
  // Paket/bundling (poin 8-9, brief bos 23 Agu 2026, aturan #3 CLAUDE.md) —
  // alasan (kalau ada) kenapa tombol Simpan masih ditahan: baris paket yang
  // stoknya habis, komponennya belum lengkap, atau IMEI-nya belum dipilih
  // semua. null = semua baris paket (kalau ada) sudah siap dikirim.
  const bundleAlasanTertahan = useMemo(
    () => cariAlasanBundleBelumSiap(form.items, resepBundlingMap, produkInfoCache),
    [form.items, resepBundlingMap, produkInfoCache]
  );
  const totalSplit = useMemo(
    () => (form.payments || []).reduce((a, p) => a + (parseFloat(p.amount) || 0), 0),
    [form.payments]
  );
  // Nilai unit yang diterima sebagai trade-in ikut MEMBAYAR transaksi. Tanpa
  // memperhitungkannya, nota trade-in selalu tampak "kurang bayar" sebesar nilai
  // unitnya dan penyimpanan diblokir — persis keluhan yang dilaporkan. Rumus ini
  // disamakan dengan InputNota.jsx yang sudah benar.
  const tradeInTotal = useMemo(
    () => (form.trade_in_items || []).reduce((a, t) => a + (parseFloat(t.harga) || 0), 0),
    [form.trade_in_items]
  );
  // Diskon & biaya admin (brief bos) — persen dihitung dari subtotal SETELAH
  // diskon, persis rumus server `_hitung_diskon_biaya_admin` di
  // routers/sales.py, supaya pratinjau di sini tidak pernah menyimpang dari
  // angka yang benar-benar disimpan.
  // D6 (brief 22 Sep): kalau ada diskon PRODUK per baris, diskon nota = jumlah
  // diskon baris (server `diskon_nota_efektif` menolak campuran keduanya).
  // Tanpa diskon baris, diskon nota lama dipakai apa adanya — dokumen lama
  // berperilaku persis seperti dulu.
  const diskonBaris = useMemo(() => jumlahDiskonBaris(form.items), [form.items]);
  const diskonNominal = diskonBaris > 0 ? diskonBaris : (form.diskon_nominal || 0);
  const biayaAdminMode = form.biaya_admin_mode || "";
  const biayaAdminNilai = form.biaya_admin_nilai || 0;
  const subtotalSetelahDiskon = Math.max(0, totalItems - diskonNominal);
  const biayaAdminRp = useMemo(() => {
    if (biayaAdminMode === "nominal") return biayaAdminNilai;
    if (biayaAdminMode === "persen") return Math.round(subtotalSetelahDiskon * biayaAdminNilai) / 100;
    return 0;
  }, [biayaAdminMode, biayaAdminNilai, subtotalSetelahDiskon]);
  // PPN (keputusan owner 18 Agu 2026) — dpp tingkat DOKUMEN = totalItems (item
  // yang diketik kasir memang sudah exclusive PPN, sama dengan
  // routers/sales.py::create_sale). Pratinjau saja, lihat PPN_PERSEN_PREVIEW.
  const kenaPpn = !!form.kena_ppn;
  // D7 (brief 22 Sep): DPP = barang − diskon PER BARIS (server `dpp_nota`);
  // diskon nota lama TIDAK mengurangi DPP. Tanpa diskon baris = rumus lama.
  const ppnPreview = useMemo(
    () => kenaPpn ? Math.round((totalItems - diskonBaris) * PPN_PERSEN_PREVIEW / 100) : 0,
    [kenaPpn, totalItems, diskonBaris]
  );
  // Diperluas dari diskon/admin (nama dipertahankan supaya diff minimal di
  // pemakai lain) — PPN memakai gerbang yang SAMA PERSIS karena efeknya
  // sama: memaksa harga_terima efektif dihitung ulang server, bukan dipakai
  // apa adanya dari ketikan kasir. Lihat `_hitung_diskon_biaya_admin` di
  // backend (parameter ppn_nominal) untuk formula lengkapnya.
  const diskonAdminAktif = diskonNominal > 0 || biayaAdminRp > 0 || kenaPpn;
  // Jebakan nilai efektif basi (lihat `hargaTerimaOverride` di atas): saat
  // sedang EDIT nota yang tadinya berdiskon/biaya-admin, dan kasir baru saja
  // menonaktifkan diskon+admin di form ini (`!diskonAdminAktif`) TANPA
  // menyentuh kolom Harga Terima secara manual (nilainya masih persis nilai
  // efektif yang dimuat openEdit), maka form.harga_terima tidak boleh dipakai
  // — itu angka lama yang sudah dipotong diskon yang kini sudah tidak ada.
  // Diperlakukan seolah kosong supaya jatuh ke fallback totalItems, sama
  // seperti kalau kasir memang tidak pernah mengisi Harga Terima manual.
  // Nota trade-in murni (tanpa diskon/admin) tidak pernah mengaktifkan
  // `hargaTerimaOverride.aktif`, jadi cabang ini tidak pernah tersentuh dan
  // rumus lama (`form.harga_terima || totalItems`) berlaku persis seperti
  // sebelumnya.
  const hargaTerimaJebakan = modalMode === "edit" && hargaTerimaOverride.aktif && !diskonAdminAktif
    && Math.abs((form.harga_terima || 0) - hargaTerimaOverride.nilaiAwal) < 0.5;
  // Nota BERTEMPO: `harga_terima` kosong berarti BELUM DIBAYAR, bukan "anggap
  // lunas". Fallback `|| totalItems` dibuat untuk nota TUNAI lama; memakainya
  // pada nota bertempo membuat `uang_diterima_nota` mengembalikan nilai penuh
  // → `total_payments` = total → piutangnya dibatalkan sebagai "lunas di
  // tempat" dan `repost_sale_to_ledger` mendebit kas yang tidak pernah
  // diterima siapa pun. Nol galat, nol log. (Temuan tinjauan 12 Sep 2026;
  // aturan yang sama sudah tertulis di `routers/sales.py::uang_diterima_nota`
  // dan di `nota_module.py` — layar ini satu-satunya yang belum mengikutinya.)
  const notaBertempo = !!(form.tempo_pembayaran || "").trim();
  const hargaTerima = hargaTerimaJebakan
    ? totalItems
    : (notaBertempo ? (Number(form.harga_terima) || 0) : (form.harga_terima || totalItems));
  // "Total harus dibayar" — SATU angka yang menjadi acuan validasi split
  // payment. Saat diskon/admin TIDAK dipakai, ini identik dengan rumus lama
  // (hargaTerima − tradeIn) supaya nota trade-in-only yang sudah ada tidak
  // berubah perilakunya sedikit pun. Saat dipakai, formulanya mengikuti
  // persis `harga_terima_efektif` di server (basis subtotal barang, BUKAN
  // hargaTerima manual — server pun mengabaikan hargaTerima manual begitu
  // diskon/admin aktif).
  const totalHarusDibayar = diskonAdminAktif
    ? Math.round((subtotalSetelahDiskon + biayaAdminRp - tradeInTotal + ppnPreview) * 100) / 100
    : hargaTerima - tradeInTotal;
  const splitBalance = totalHarusDibayar - totalSplit;

  // ---- Penjualan FORMAT BARU (hasil Input Nota, `pembayaran_rinci`) ----
  // Angka "harus dibayar" dihitung SERVER lewat pratinjau yang sama dengan
  // Input Nota. DP-nya sudah terpotong nota ini, jadi dikirim sebagai
  // `dp_tetap` (angka tersimpan di dokumen, persis yang dipakai `update_sale`
  // saat mencocokkan baris) — bukan dibaca ulang dari dokumen DP yang sisanya
  // sudah berkurang. Rumus layar di atas (`totalHarusDibayar`) tidak mengenal
  // DP dan akan menolak edit yang sah.
  const rinciEdit = modal && modalMode === "edit" && !!form.pembayaran_rinci;
  const kunciPratinjauRinci = useMemo(() => {
    if (!rinciEdit) return null;
    return JSON.stringify({
      items: form.items.map(it => ({
        line_id: it.line_id, addition: it.addition, box: it.box, qty: it.qty || 1,
          nama_barang: it.nama_barang || "", harga: angka(it.harga), diskon: angka(it.diskon),
        sku: it.sku || null, qty: it.qty || 1, is_bonus: !!it.is_bonus,
      })),
      kena_ppn: kenaPpn,
      trade_in_items: (form.trade_in_items || []).filter(t => (t.nama || "").trim())
        .map(t => ({ nama: t.nama, harga: angka(t.harga) })),
      dp_tetap: angka(form.dp_nominal),
      no_hp: form.no_hp || "",
      payments: (form.payments || []).map(p => ({
        method: p.method || "", amount: angka(p.amount), keterangan: p.keterangan || null,
      })),
      tempo_pembayaran: form.tempo_pembayaran || null,
      // Diskon NOTA tersimpan (penjualan yang lahir dari draf lama berdiskon)
      // ikut dihitung — tanpa ini pratinjau menuntut pembayaran sebesar
      // diskonnya, dan "Isi sisa" melahirkan kas fiktif (tinjauan 23 Sep).
      diskon: diskonPratinjauEditRinci({ items: form.items, diskon_nominal: form.diskon_nominal }),
    });
  }, [rinciEdit, form.items, kenaPpn, form.trade_in_items, form.dp_nominal, form.no_hp,
      form.payments, form.tempo_pembayaran, form.diskon_nominal]);
  const [pratinjauRinci, setPratinjauRinci] = useState({ kunci: null, data: null, gagal: null });
  useEffect(() => {
    if (!kunciPratinjauRinci) return;
    let batal = false;
    const t = setTimeout(async () => {
      try {
        const { data } = await api.post("/nota/sales/pratinjau", JSON.parse(kunciPratinjauRinci));
        if (!batal) setPratinjauRinci({ kunci: kunciPratinjauRinci, data, gagal: null });
      } catch (e) {
        if (!batal) setPratinjauRinci({ kunci: kunciPratinjauRinci, data: null,
                                        gagal: pesanGagalDariServer(e, "Gagal menghitung tagihan") });
      }
    }, 350);
    return () => { batal = true; clearTimeout(t); };
  }, [kunciPratinjauRinci]);
  const rinciSegar = !!kunciPratinjauRinci && pratinjauRinci.kunci === kunciPratinjauRinci && !!pratinjauRinci.data;
  const rincianRinci = rinciSegar ? pratinjauRinci.data.rincian : null;
  const pesanRinci = rinciSegar ? (pratinjauRinci.data.pesan || []) : [];
  // Metode "Trade-in" tidak ditawarkan untuk baris pembayaran format baru —
  // server menolaknya (`bersihkan_baris_pembayaran`).
  const paymentGroupsRinci = useMemo(() => Object.fromEntries(
    Object.entries(paymentGroups || {})
      .map(([g, list]) => [g, (list || []).filter(m => !metodeTradeIn(m.name))])
      .filter(([, list]) => list.length)
  ), [paymentGroups]);

  const updItem = (i, k, v) => setForm(s => {
    const ni = [...s.items];
    ni[i] = { ...ni[i], [k]: (k === "harga" || k === "diskon") ? (parseFloat(v) || 0) : v };
    // Auto-derive kode dari kata pertama nama_barang. Kode tidak ditampilkan ke user.
    // Kategori ikut disarankan ulang HANYA sebagai cadangan teks bebas (baris
    // tanpa SKU) — baris ber-SKU tidak pernah lewat jalur ini, lihat
    // ItemNamaField.pilih() yang memakai kategori master produk apa adanya.
    if (k === "nama_barang") { ni[i].kode = deriveKode(v); ni[i].kategori = deriveKategoriTeksBebas(v); }
    return { ...s, items: ni };
  });
  // Tempel beberapa field item sekaligus — dipakai autocomplete produk supaya
  // nama_barang, sku, harga, dan kategori terisi dalam satu update (tidak
  // saling menimpa lewat beberapa panggilan updItem berurutan).
  const patchItem = (i, patch) => setForm(s => {
    const ni = [...s.items];
    // F3: produk paket yang dipilih mencatat harga katalognya sebagai acuan
    // kunci harga (lihat `hargaPaketTerkunci`, lib/notaPenjualan.js).
    ni[i] = { ...ni[i], ...patchBarisDenganKunciPaket(patch, resepBundlingMap) };
    return { ...s, items: ni };
  });
  // Kontrak G2 bagian C: memilih unit ber-IMEI dari ItemCariField sudah
  // menunjukkan gudang aslinya — satu transaksi = satu gudang (kontrak G1
  // #4), jadi diisikan ke sini otomatis kalau belum ada gudang terpilih
  // (tidak pernah MENIMPA pilihan yang sudah dibuat sendiri).
  const onWarehouseHint = (wid) => setForm(s => (!s.warehouse_id ? { ...s, warehouse_id: wid } : s));
  const addItem = () => setForm(s => ({
    ...s, items: [...s.items, { nama_barang: "", harga: 0, diskon: 0, kode: "HP", kategori: KATEGORI_DEFAULT, sku: "", imei: "", is_bonus: false, non_stok: false, komponen: null }],
  }));
  const rmItem = (i) => setForm(s => ({ ...s, items: s.items.filter((row, x) => x !== i && row.addition?.parent_line_id !== s.items[i].line_id) }));

  const updPay = (i, k, v) => setForm(s => {
    const np = [...(s.payments || [])];
    np[i] = { ...np[i], [k]: k === "amount" ? (parseFloat(v) || 0) : v };
    return { ...s, payments: np };
  });
  const addPay = () => setForm(s => ({
    ...s,
    payments: [...(s.payments || []), {
      method: paymentMethods[0]?.name || "Cash",
      // Format baru: sisa hitungan SERVER; format lama: rumus lama.
      amount: Math.max(0, s.pembayaran_rinci
        ? (rincianRinci ? rincianRinci.harus_dibayar - totalSplit : 0)
        : splitBalance),
      keterangan: "",
    }],
  }));
  const rmPay = (i) => setForm(s => ({ ...s, payments: (s.payments || []).filter((_, x) => x !== i) }));

  const openCreate = () => {
    setModalMode("create");
    setForm(emptyForm());
    setHargaTerimaOverride({ aktif: false, nilaiAwal: 0 });
    setModal(true);
  };

  const openEdit = (sale) => {
    setModalMode("edit");
    // Tandai jebakan nilai efektif SEBELUM setForm: sale.harga_terima yang
    // akan dimuat ke form di bawah adalah hasil hitungan server (subtotal -
    // diskon + admin) kalau nota ini sedang pakai diskon dan/atau biaya
    // admin — lihat komentar panjang di `hargaTerimaJebakan`.
    setHargaTerimaOverride({
      aktif: (sale.diskon_nominal || 0) > 0 || (sale.biaya_admin_rp || 0) > 0 || (sale.ppn_nominal || 0) > 0,
      nilaiAwal: sale.harga_terima || 0,
    });
    setForm({
      id: sale.id,
      tanggal: sale.tanggal || hariIniISO(),
      waktu: sale.waktu || nowTime(),
      nama_pelanggan: sale.nama_pelanggan || "",
      no_hp: sale.no_hp || "",
      // B2 (brief 22 Sep): nota B2B/USER yang tersimpan dianggap sudah
      // memilih kartu; kalau ternyata tidak cocok kartu mana pun (nota lama
      // ketik-bebas), server menolak simpannya dengan pesan yang menyebut
      // kartunya dan kasir memilih ulang lewat daftar.
      _kartu_pelanggan: ["b2b", "user"].includes(sale.jenis_pelanggan) && sale.nama_pelanggan && sale.no_hp
        ? { nama: sale.nama_pelanggan, no_hp: sale.no_hp } : null,
      // F5 (brief owner 12 Sep 2026) — Jenis Pelanggan & Metode Pembayaran/
      // Tempo boleh diedit pada nota B2B/USER bertempo. `_boleh_edit_tempo`
      // datang dari server (routers/sales.py::get_sale), BUKAN ditebak layar:
      // ia sudah memperhitungkan izin akun DAN apakah piutangnya sudah
      // menerima pembayaran.
      _boleh_edit_tempo: !!sale._boleh_edit_tempo,
      jenis_pelanggan: sale.jenis_pelanggan || "umum",
      tempo_pembayaran: sale.tempo_pembayaran || "",
      pic_approval_piutang: sale.pic_approval_piutang || "",
      items: (sale.items && sale.items.length ? sale.items : [{ nama_barang: "", harga: 0, kode: "HP" }])
        // Baris SINTETIK per komponen paket (`is_bundle_component: true`,
        // harga 0) dibangun otomatis oleh backend (`ekspansi_komponen_bundle_
        // untuk_penjualan`, routers/inventory/bundling.py) dan TIDAK PERNAH
        // boleh muncul sebagai baris tersendiri yang bisa disunting — kasir
        // akan mengira ada barang gratis nyasar dan menghapusnya, merusak
        // komposisi paket. Sudah terwakili lewat `komponen[]` di baris
        // paketnya sendiri (poin 10, sama seperti FakturPrint.jsx menyaring
        // baris ini untuk cetakan).
        .filter(it => !it.is_bundle_component)
        .map(it => ({
          line_id: it.line_id, addition: it.addition, box: it.box, qty: it.qty || 1,
          nama_barang: it.nama_barang || "", harga: it.harga || 0, kode: it.kode || "HP",
          // Diskon produk per baris (D5) WAJIB ikut termuat — tanpanya, menyimpan
          // ulang penjualan hasil Input Nota menghapus diskon yang sudah dijurnal
          // ke 4190. Dokumen lama tanpa medan ini = 0.
          diskon: it.diskon || 0,
          // Transaksi lama dari SEBELUM field kategori ada (bukan alias lama —
          // itu sudah kunci yang sah): tebak sekali dari teks sebagai cadangan
          // terakhir supaya dropdown tidak kosong. Transaksi yang SUDAH punya
          // kategori (kanonik ATAU alias) dipakai apa adanya, tidak ditebak ulang.
          kategori: it.kategori || deriveKategoriTeksBebas(it.nama_barang || ""),
          // SKU yang sudah tersimpan WAJIB ikut termuat — kalau tidak, edit transaksi
          // lama akan menghapus tautan katalognya begitu disimpan ulang.
          sku: it.sku || "",
          // IMEI per baris (brief bos poin 4) — transaksi lama tanpa field ini
          // (dibuat sebelum fitur ada) memuat "" seperti biasa, tetap bisa
          // dibuka/disimpan ulang tanpa IMEI (kompatibel mundur).
          imei: it.imei || "",
          // Penanda barang bonus (backend menolak harga 0 tanpa penanda ini) —
          // nota lama tanpa field ini dianggap bukan bonus (default false).
          is_bonus: !!it.is_bonus,
          // Baris non-stok eksplisit (kontrak G1 keputusan owner #2) — nota
          // lama (sebelum field ini ada) dianggap bukan non-stok; kalau
          // barisnya memang tanpa SKU, kasir wajib menandainya ulang sebelum
          // bisa disimpan lagi (aturan baru, bukan pelonggaran data lama).
          non_stok: !!it.non_stok,
          // Snapshot komponen paket (poin 8/10) — dimuat APA ADANYA supaya
          // nota lama yang disimpan ulang TANPA kasir menyentuh baris ini
          // tetap mengirim komposisi yang sama persis. Dinormalkan ke bentuk
          // minimal kontrak `KomponenBundleItem` (sku/qty/imei) — field
          // tambahan snapshot (nama/kategori/bundle_def_id) hanya untuk
          // tampilan, tidak dibaca backend.
          komponen: Array.isArray(it.komponen) && it.komponen.length
            ? it.komponen.map(k => ({ sku: k.sku, qty: k.qty, imei: k.imei || null }))
            : null,
        })),
      imei: sale.imei || "",
      metode_pembayaran: sale.metode_pembayaran || "Cash",
      trade_in: !!sale.trade_in,
      trade_in_note: sale.trade_in_note || "",
      // WAJIB ikut dimuat: dulu daftar unit trade-in tidak pernah dibaca ke form
      // dan juga tidak dikirim ulang saat menyimpan, sehingga sekali nota
      // trade-in diedit, unit + IMEI-nya lenyap dari basis data.
      // Sub-kolom unit (status/warna/BH/kelengkapan/catatan) IKUT dimuat —
      // dulu hanya nama/harga/imei, sehingga menyimpan ulang nota trade-in
      // menghapus kondisi unitnya dari dokumen (PATCH menimpa daftar unit utuh),
      // dan nota format baru ditolak karena kolom wajib trade-in kosong.
      trade_in_items: (sale.trade_in_items || []).map(t => ({
        ...t,
        nama: t.nama || t.nama_barang || "", harga: t.harga || 0, imei: t.imei || "",
        status: t.status || "", warna: t.warna || "", kelengkapan: t.kelengkapan || "",
      })),
      harga_terima: sale.harga_terima || 0,
      catatan: sale.catatan || "",
      nama_penjual: sale.nama_penjual || "",
      branch: sale.branch || "",
      // Muat ulang diskon & biaya admin saat edit — tanpa ini, membuka lalu
      // menyimpan ulang nota yang sudah pakai diskon akan menghapusnya diam-diam.
      // Format baru: yang dimuat hanya diskon NOTA (bukan angka turunan diskon
      // baris) — lihat `diskonNotaTersimpan`.
      diskon_nominal: sale.pembayaran_rinci ? diskonNotaTersimpan(sale) : (sale.diskon_nominal || 0),
      diskon_pic_approval: sale.diskon_pic_approval || "",
      biaya_admin_mode: sale.biaya_admin_mode || "",
      biaya_admin_nilai: sale.biaya_admin_nilai || 0,
      // Nota lama (sebelum fitur PPN ada) tidak punya field ini -> default
      // false, kompatibel mundur.
      kena_ppn: !!sale.kena_ppn,
      // Format baru (D4): baris pembayaran SELALU tampil sebagai baris (tanpa
      // toggle Split) — satu baris pun tetap baris bernominal. DP ikut dimuat
      // HANYA untuk pratinjau (`dp_tetap`); PATCH tidak mengirimnya.
      pembayaran_rinci: !!sale.pembayaran_rinci,
      dp_nominal: sale.dp_nominal || 0,
      is_split: !!sale.pembayaran_rinci || !!(sale.payments && sale.payments.length > 1),
      // `keterangan` (No. Kontrak, D3) WAJIB ikut termuat — tanpanya PATCH
      // menimpa baris pembayaran dan keterangannya hilang.
      payments: sale.payments && sale.payments.length
        ? sale.payments.map(p => ({ method: p.method, amount: p.amount, keterangan: p.keterangan || "" }))
        : [{ method: sale.metode_pembayaran || "Cash",
             amount: sale.pembayaran_rinci ? 0 : (sale.harga_terima || 0), keterangan: "" }],
      // Transaksi lama (sebelum fitur Sumber Info ada, 11 Agu 2026) tidak
      // punya field ini -> "" (kosong). Backend TETAP mempertahankan nilai
      // lama kalau PATCH ini tidak menyentuhnya sama sekali (lihat komentar
      // `eff_source_info` di routers/sales.py update_sale) — tapi form ini
      // SELALU mengirim field-nya (lihat save()), jadi mengosongkan di sini
      // berarti kasir harus memilih ulang sebelum menyimpan perubahan apa pun,
      // konsisten dengan pagar hard-stop yang sama dipakai transaksi baru.
      source_info: sale.source_info || "", source_ref_id: sale.source_ref_id || "",
      // Nota lama (sebelum kontrak G1) tidak punya field ini -> "" (kosong),
      // backend menurunkan gudangnya sendiri seperti sebelum fitur ini ada.
      warehouse_id: sale.warehouse_id || "",
    });
    setDetail(null); setDetailId(null);
    setModal(true);
  };

  const save = async () => {
    if (!form.nama_pelanggan || !form.nama_penjual) { toast.error("Nama pelanggan & penjual wajib"); return; }
    // B2: cermin aturan server (`pelanggan.kanonikal_pelanggan_nota`).
    if (perluKartuPelanggan && !kartuCocok(form._kartu_pelanggan, form.nama_pelanggan, form.no_hp)) {
      toast.error("Pelanggan B2B/USER wajib dipilih dari daftar — atau daftarkan lewat \"+ Pelanggan baru\".");
      return;
    }
    if (form.items.some(x => !x.nama_barang)) { toast.error("Nama barang tiap item wajib"); return; }
    // Sumber Info (brief bos 11 Agu 2026) — cermin persis aturan backend
    // (_validasi_dan_lengkapi_source_info) supaya kasir dicegat DI SINI,
    // bukan menabrak 400 dari server setelah menekan "Simpan".
    const errSourceInfo = validasiSourceInfo(form.source_info, form.source_ref_id, sourceInfoCfg);
    if (errSourceInfo) { toast.error(errSourceInfo); return; }
    // Backend menolak baris berharga Rp 0 kecuali ditandai bonus (brief bos) —
    // dicegat di sini dulu supaya kasir tidak menabrak error 400 dari server
    // tanpa tahu jalan keluarnya.
    const itemGratisTanpaTanda = form.items.find(x => x.nama_barang && (parseFloat(x.harga) || 0) === 0 && !x.is_bonus);
    if (itemGratisTanpaTanda) {
      toast.error(`Harga "${itemGratisTanpaTanda.nama_barang}" masih Rp0 — centang "Bonus/Gratis" kalau barang ini memang diberikan cuma-cuma, atau isi harga jualnya.`);
      return;
    }
    // Baris ketik-bebas tanpa SKU WAJIB ditandai eksplisit non-stok (kontrak
    // G1 keputusan owner #2) — cermin persis aturan backend
    // (create_sale/update_sale) supaya kasir dicegat DI SINI dengan
    // penjelasan, bukan menabrak 400 generik.
    const itemTanpaSkuTanpaTanda = form.items.find(x => x.nama_barang && !x.sku && !x.non_stok);
    if (itemTanpaSkuTanpaTanda) {
      toast.error(`"${itemTanpaSkuTanpaTanda.nama_barang}" belum terhubung ke katalog stok — pilih barang dari hasil pencarian, atau centang "Non-stok (jasa/barang luar)".`);
      return;
    }
    // Satu nota = satu gudang (kontrak G1 keputusan owner #4) — kalau kasir
    // berhak >1 gudang dan ada baris ber-SKU (yang memang mengikat ke stok
    // gudang tertentu), gudang wajib dipilih di sini. Barang non-stok murni
    // tidak pernah menyentuh stok jadi tidak butuh gudang.
    if (tampilkanPilihGudang && !form.warehouse_id && form.items.some(x => x.sku)) {
      toast.error("Pilih gudang untuk transaksi ini — ada barang ber-SKU yang mengikat ke stok gudang tertentu.");
      return;
    }
    // Satu unit tidak boleh terpilih di dua baris sekaligus DALAM SATU
    // transaksi ini (backend TIDAK menegakkan ini — `_tolak_stok_kosong`
    // memvalidasi tiap baris independen, lihat catatan di ItemImeiField).
    // Ini pagar terakhir sebelum terkirim; pemilihnya sendiri sudah
    // menonaktifkan opsi yang sama di baris lain, jadi ini seharusnya jarang
    // tersentuh.
    const imeiTerpakai = form.items.map(x => (x.imei || "").trim()).filter(Boolean);
    const imeiDobel = imeiTerpakai.find((v, idx) => imeiTerpakai.indexOf(v) !== idx);
    if (imeiDobel) {
      toast.error(`IMEI ${imeiDobel} dipilih di lebih dari satu baris — satu unit hanya boleh dijual sekali dalam satu transaksi.`);
      return;
    }
    // Paket/bundling (poin 8-9, brief bos 23 Agu 2026) — aturan #3 CLAUDE.md:
    // jangan biarkan kasir menekan Simpan lalu menabrak 400 dari backend.
    // Tombol Simpan sudah ditahan (lihat `disabled` di bawah) untuk kasus
    // yang SUDAH terlihat sebelum submit; cek ini jaring pengaman terakhir
    // (mis. race saat stok/produkInfoCache berubah tepat sebelum klik).
    const alasanBundle = cariAlasanBundleBelumSiap(form.items, resepBundlingMap, produkInfoCache);
    if (alasanBundle) { toast.error(alasanBundle); return; }
    // Diskon PRODUK per baris (D5/D6) — 0..harga, satu PIC per nota. Cermin
    // `validasi_diskon_baris` server supaya kasir dicegat dengan nama barangnya.
    const errDiskon = cekDiskonBaris(form.items, form.diskon_pic_approval);
    if (errDiskon) { toast.error(errDiskon); return; }
    // Format baru: kecocokan uang dari pratinjau SERVER (sama dengan Input Nota).
    if (form.pembayaran_rinci && modalMode === "edit") {
      if (!rinciSegar) {
        toast.error(pratinjauRinci.gagal || "Tagihan masih dihitung — tunggu sebentar lalu tekan lagi.");
        return;
      }
      if (!pratinjauRinci.data.cocok) {
        toast.error(pesanRinci[0] || "Jumlah pembayaran belum sesuai dengan yang harus dibayar.");
        return;
      }
    }

    const payload = {
      tanggal: form.tanggal,
      waktu: form.waktu || nowTime(),
      nama_pelanggan: form.nama_pelanggan,
      no_hp: form.no_hp,
      // Medan penanda layar (`_harga_paket`) bukan medan dokumen.
      items: form.items.map(buangMedanLayar),
      imei: form.imei,
      metode_pembayaran: form.metode_pembayaran,
      trade_in: form.trade_in,
      trade_in_note: form.trade_in_note,
      trade_in_items: form.trade_in_items || [],
      // Saat jebakan aktif, kirim 0 (bukan `hargaTerima`, yang di sini sudah
      // sama dengan totalItems) — 0 membuat server jatuh ke `total_harga`
      // (lihat routers/sales.py `update["total_payments"] = update.get(
      // "harga_terima") or total_harga`). Kirim eksplisit 0 lebih aman
      // daripada mengandalkan totalItems === total_harga server persis sama,
      // walau keduanya dijamin identik di sini.
      harga_terima: hargaTerimaJebakan ? 0 : hargaTerima,
      catatan: form.catatan,
      nama_penjual: form.nama_penjual,
      branch: form.branch,
      // Diskon & biaya admin (brief bos) — "" (tanpa) dikirim sebagai null
      // supaya server tahu fiturnya memang tidak dipakai (kompatibel mundur).
      // D6: dengan diskon per baris, `diskonNominal` = jumlah diskon baris
      // (angka turunan yang sama dengan server), bukan diskon nota kedua.
      diskon_nominal: diskonNominal,
      diskon_pic_approval: (form.diskon_pic_approval || "").trim() || null,
      biaya_admin_mode: biayaAdminMode || null,
      biaya_admin_nilai: biayaAdminNilai,
      // PPN (keputusan owner 18 Agu 2026) — server yang menghitung
      // ppn_persen/ppn_nominal/dpp sesungguhnya, ini cuma togglenya.
      kena_ppn: kenaPpn,
      // Sumber Info / atribusi marketing (brief bos 11 Agu 2026) — sudah
      // divalidasi client-side di atas (validasiSourceInfo), server tetap
      // menegakkan ulang (_validasi_dan_lengkapi_source_info).
      source_info: form.source_info || null,
      source_ref_id: form.source_ref_id || null,
      // Satu nota = satu gudang (kontrak G1) — "" -> null, backend menurunkan
      // gudangnya sendiri (dari unit IMEI yang dipilih, atau satu-satunya
      // gudang yang boleh dipakai kasir ini).
      warehouse_id: form.warehouse_id || null,
    };

    // F5: ketiga medan tempo HANYA dikirim saat blok editnya memang tampil.
    // Kalau selalu dikirim, setiap edit nota tunai biasa ikut membawa
    // `jenis_pelanggan: "umum"` dan `tempo_pembayaran: null` — dan PATCH ini
    // memakai `exclude_unset`, jadi mengirimnya berarti MENIMPA nilai yang
    // ada. Nota B2B yang diedit kasir lewat jalur lain akan kehilangan
    // temponya tanpa ada yang menekan apa pun.
    if (modalMode === "edit" && form._boleh_edit_tempo) {
      payload.jenis_pelanggan = form.jenis_pelanggan || "umum";
      payload.tempo_pembayaran = form.tempo_pembayaran || null;
      payload.pic_approval_piutang = (form.pic_approval_piutang || "").trim() || null;
    }

    if (form.pembayaran_rinci && modalMode === "edit") {
      // Format baru (D4): baris pembayaran (+ keterangan) SELALU dikirim — PATCH
      // yang tidak membawa `payments` membiarkan baris lama, dan server menolak
      // dokumen format baru yang dikirim kembali ke format lama. `harga_terima`
      // bukan uang di format ini; biaya admin tidak dipungut lagi (D4).
      const pays = (form.payments || [])
        .filter(p => (parseFloat(p.amount) || 0) > 0)
        .map(p => ({ method: p.method, amount: parseFloat(p.amount) || 0,
                     keterangan: (p.keterangan || "").trim() || null }));
      payload.pembayaran_rinci = true;
      payload.payments = pays;
      payload.metode_pembayaran = pays[0]?.method || form.metode_pembayaran || "Cash";
      payload.harga_terima = 0;
      payload.biaya_admin_mode = null;
      payload.biaya_admin_nilai = 0;
      // Diskon baris kalau ada; kalau tidak, diskon nota tersimpan APA ADANYA.
      // Dulu selalu `diskonBaris` (0 untuk penjualan dari draf lama berdiskon
      // nota): diskonnya hilang dari jurnal saat kasir hanya mengubah catatan.
      payload.diskon_nominal = diskonKirimEditRinci(form);
    } else if (form.is_split) {
      const pays = (form.payments || []).filter(p => p.method && (parseFloat(p.amount) || 0) > 0)
        .map(p => ({ ...p, keterangan: (p.keterangan || "").trim() || null }));
      if (pays.length === 0) { toast.error("Isi minimal 1 baris split payment"); return; }
      const sum = pays.reduce((a, p) => a + p.amount, 0);
      // "Total harus dibayar" sudah memperhitungkan trade-in (dan diskon/biaya
      // admin kalau dipakai) — lihat definisi `totalHarusDibayar` di atas.
      // Tanpa memakai angka ini, setiap nota trade-in/diskon ditolak split-nya.
      const wajibTunai = totalHarusDibayar;
      if (Math.abs(wajibTunai - sum) > 0.5) {
        const rincian = diskonAdminAktif
          ? ` (subtotal ${idr(totalItems)}` +
            (diskonNominal > 0 ? ` − diskon ${idr(diskonNominal)}` : "") +
            (biayaAdminRp > 0 ? ` + admin ${idr(biayaAdminRp)}` : "") +
            (kenaPpn ? ` + PPN ${idr(ppnPreview)}` : "") +
            (tradeInTotal > 0 ? ` − trade-in ${idr(tradeInTotal)}` : "") + ")"
          : (tradeInTotal > 0 ? ` (harga ${idr(hargaTerima)} − trade-in ${idr(tradeInTotal)})` : "");
        toast.error(`Total pembayaran ${idr(sum)} belum menutup ${idr(wajibTunai)}${rincian}`);
        return;
      }
      payload.payments = pays;
      payload.metode_pembayaran = pays.length === 1 ? pays[0].method : "Split";
    }

    // PPN (brief bos): dialog konfirmasi sebelum simpan — supaya kasir sadar
    // transaksi ini akan menambahkan kewajiban pajak (2400 PPN Keluaran)
    // sebelum benar-benar terkirim.
    if (kenaPpn) {
      setPpnConfirm(payload);
      return;
    }
    await doSaveSale(payload);
  };

  const doSaveSale = async (payload) => {
    setSaving(true);
    try {
      let res;
      if (modalMode === "edit" && form.id) {
        res = await api.patch(`/sales/${form.id}`, payload);
        toast.success("Perubahan tersimpan");
      } else {
        res = await api.post("/sales", payload);
        toast.success("Penjualan tercatat");
      }
      // Peringatan non-blokir "dijual di bawah modal" (brief bos 21 Agu #B1,
      // gantikan price floor per kategori yang dicabut) — transaksi TETAP
      // tersimpan (lihat toast.success di atas), ini murni alarm, pola sama
      // dengan `peringatan_harga` di InventoryPO.jsx. SENGAJA tidak menyebut
      // angka modal (keputusan privasi owner) — hanya nama/SKU barangnya.
      (res?.data?.peringatan_di_bawah_modal || []).forEach(p =>
        toast.warning(`${p.nama_barang || p.sku} dijual di bawah modal`, { duration: 8000 }));
      setModal(false);
      setForm(emptyForm());
      setPpnConfirm(null);
      load();
    } catch (e) {
      // Kontrak G1: create_sale gagal posting stok/jurnal -> transaksi TIDAK
      // tersimpan, tapi arsip kegagalan tetap tertulis (koleksi `sales_gagal`)
      // supaya bisa ditelusuri. Pesan generik "Gagal" membuang jejak itu —
      // tampilkan alasan server + kode arsip apa adanya, durasi lebih panjang
      // karena pesannya lebih penting dibaca sampai selesai.
      // Kecuali kasus gudang buntu (400 "Pilih gudang..." tapi picker-nya
      // sendiri tidak tampil di form ini) — pesan itu diarahkan ke jalan
      // keluar, bukan disuruh memilih sesuatu yang tak pernah ada di layar.
      toast.error(
        pesanGudangButaArah(e, tampilkanPilihGudang) || pesanGagalDariServer(e, "Gagal menyimpan penjualan"),
        { duration: 10000 },
      );
    } finally {
      setSaving(false);
    }
  };

  const doDeleteSale = async (id, pin) => {
    await api.delete(`/sales/${id}`, pin ? { params: { pin } } : undefined);
    setDetailId(null); setDetail(null);
    load();
  };

  // Brief #23 — DELETE bisa membalas 428 (butuh PIN persetujuan). Alurnya:
  // ajukan PIN otomatis lewat /aktivitas/hapus/ajukan, lalu buka dialog PIN
  // supaya pengguna tinggal menempelkan kode yang diberikan pemberi izin.
  const del = async (id) => {
    if (!confirm("Yakin hapus penjualan ini?")) return;
    try {
      await doDeleteSale(id);
    } catch (e) {
      if (e?.response?.status === 428) {
        try {
          await api.post("/aktivitas/hapus/ajukan", {
            modul: "sales", target_id: id,
            ringkasan: `Hapus penjualan ${detail?.nama_pelanggan || ""}`.trim(),
          });
        } catch (e2) {
          toast.error(e2?.response?.data?.detail || "Gagal mengajukan izin hapus");
          return;
        }
        setPinDialog({ pin: "", submitting: false, error: "", retry: (pin) => doDeleteSale(id, pin) });
      } else {
        toast.error(e?.response?.data?.detail || "Gagal menghapus penjualan");
      }
    }
  };

  const submitPinDialog = async () => {
    if (!pinDialog) return;
    setPinDialog(d => ({ ...d, submitting: true, error: "" }));
    try {
      await pinDialog.retry(pinDialog.pin);
      setPinDialog(null);
    } catch (e) {
      if (e?.response?.status === 403) {
        setPinDialog(d => ({ ...d, submitting: false, error: "PIN salah atau sudah kedaluwarsa — coba lagi." }));
      } else {
        toast.error(e?.response?.data?.detail || "Gagal menghapus");
        setPinDialog(d => ({ ...d, submitting: false }));
      }
    }
  };

  // ---- Ajukan Retur Penjualan (brief bos) — dibuka dari panel detail
  // transaksi; approval sesungguhnya terjadi di /retur (agen lain). ----
  // Default nominal retur — prorata NETO, bukan bruto. `r.harga` per baris
  // adalah harga JUAL asli (belum dipotong diskon nota), jadi menjumlahkannya
  // apa adanya menyarankan refund lebih besar dari uang yang benar-benar
  // diterima untuk nota berdiskon (over-refund). `faktorNeto` = (total_harga
  // − diskon_nominal) / total_harga meratakan proporsi diskon ke tiap baris.
  // `faktorPpn` (18 Agu 2026, brief PPN) menambahkan porsi PPN nota ini
  // secara proporsional — nota ber-PPN membuat pelanggan membayar DPP+PPN,
  // jadi saran refund yang berhenti di DPP saja akan selalu kurang dari uang
  // yang sungguh diterima (lihat `_neto_diterima_sale` di backend, satu
  // sumber kebenaran yang sama dipakai cap `nominal_kembali` DAN pembagian
  // proporsional jurnal saat approve). Hasilnya di-clamp ke `sisaNilaiMaks`
  // (sisa hak retur nota ini setelah dikurangi retur aktif lain, JUGA sudah
  // mencakup PPN) supaya saran tidak pernah melebihi itu. Ini HANYA
  // memengaruhi nominal SARAN — harga per baris yang dikirim ke backend
  // (`submitRetur`) tetap index_item/qty/alasan, tidak berubah.
  // Rumusnya kini di `lib/retur.js` (`hitungNominalReturDefault`) — dipakai
  // juga oleh menu "Ajukan Retur Penjualan" di halaman Retur (P18a, 4 Sep 2026).

  const openReturModal = async (sale) => {
    setReturModal({
      loading: true, submitting: false, saleId: sale.id,
      items: [], nominal_kembali: 0, nominalTouched: false, akun: "", catatan: "",
      faktorNeto: 1, faktorPpn: 0, sisaNilaiMaks: 0, diskonNominal: sale.diskon_nominal || 0,
      kenaPpn: !!sale.kena_ppn, ppnNominalSale: sale.kena_ppn ? (sale.ppn_nominal || 0) : 0,
      // I1 (brief 22 Sep): kredit tukar unit melekat ke nama DAN No HP. Nota
      // tanpa No HP wajib mengisinya di sini (lihat retur.py).
      saleNoHp: (sale.no_hp || "").trim(), noHpKredit: "",
    });
    try {
      // GET /retur/penjualan butuh izin retur.view — pemegang retur.create
      // SAJA (tanpa view) akan kena 403 di panggilan ini. Itu bukan alasan
      // modal ini gagal dibuka: daftar retur aktif di sini cuma dipakai
      // sebagai BATAS UX (qty/nominal maksimum), server tetap validator akhir
      // saat POST. Jadi 403 khusus panggilan ini diperlakukan sebagai "belum
      // ada retur aktif" (list kosong), bukan error yang menutup modal.
      const [kbRes, existingRes] = await Promise.all([
        kasBankAccounts.length ? Promise.resolve({ data: kasBankAccounts }) : api.get("/finance/accounts/kas-bank"),
        api.get("/retur/penjualan", { params: { sale_id: sale.id } })
          .catch(e => {
            if (e?.response?.status === 403) return { data: { items: [] } };
            throw e;
          }),
      ]);
      if (!kasBankAccounts.length) setKasBankAccounts(kbRes.data || []);
      // Baris retur + faktor prorata disusun oleh SATU sumber (lib/retur.js,
      // `susunReturDariNota`) — sama persis dengan yang dipakai halaman Retur.
      // Penjelasan soal isi paket/komponen ada di sana.
      const susun = susunReturDariNota(sale, existingRes.data?.items || []);
      setReturModal(m => ({
        ...m, loading: false, items: susun.rows, faktorNeto: susun.faktorNeto, faktorPpn: susun.faktorPpn,
        sisaNilaiMaks: susun.sisaNilaiMaks, diskonNominal: susun.diskonNominal,
        diskonPerBaris: susun.diskonPerBaris,
        kenaPpn: susun.kenaPpn, ppnNominalSale: susun.ppnNominalSale,
      }));
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Gagal memuat data retur");
      setReturModal(null);
    }
  };

  const toggleReturItem = (i, checked) => setReturModal(m => {
    const items = m.items.map((r, idx) => idx === i
      ? { ...r, checked, qtyRetur: checked ? (r.qtyRetur || Math.min(1, r.sisa)) : r.qtyRetur }
      : r);
    return {
      ...m, items,
      nominal_kembali: m.nominalTouched ? m.nominal_kembali
        : hitungNominalReturDefault(items, m.faktorNeto, m.faktorPpn, m.sisaNilaiMaks),
    };
  });
  const setReturQty = (i, v) => setReturModal(m => {
    const batas = m.items[i]?.sisa || 0;
    const qty = Math.max(0, Math.min(batas, parseInt(v, 10) || 0));
    const items = m.items.map((r, idx) => idx === i ? { ...r, qtyRetur: qty } : r);
    return {
      ...m, items,
      nominal_kembali: m.nominalTouched ? m.nominal_kembali
        : hitungNominalReturDefault(items, m.faktorNeto, m.faktorPpn, m.sisaNilaiMaks),
    };
  });
  const setReturAlasan = (i, v) => setReturModal(m => ({
    ...m, items: m.items.map((r, idx) => idx === i ? { ...r, alasan: v } : r),
  }));

  const submitRetur = async () => {
    if (!returModal) return;
    const dipilih = returModal.items.filter(r => r.checked && r.qtyRetur > 0);
    if (dipilih.length === 0) { toast.error("Pilih minimal 1 barang untuk diretur"); return; }
    if (dipilih.some(r => !r.alasan.trim())) { toast.error("Alasan retur wajib diisi tiap barang yang dipilih"); return; }
    // Akun kas hanya wajib untuk pengembalian UANG. "Tukar unit" tidak
    // mengeluarkan uang sama sekali hari itu — nilainya ditahan jadi kredit
    // milik pelanggan.
    if (returModal.jenisKembali !== "tukar_unit" && !returModal.akun) {
      toast.error("Pilih akun pengembalian uang"); return;
    }
    if (!(returModal.nominal_kembali > 0)) { toast.error("Nominal kembali harus lebih dari 0"); return; }
    const perluHpKredit = returModal.jenisKembali === "tukar_unit" && !returModal.saleNoHp;
    if (perluHpKredit && (returModal.noHpKredit || "").replace(/\D/g, "").length < 10) {
      toast.error("Nota ini tidak punya No HP — isi No HP pelanggan (minimal 10 digit) supaya kreditnya bisa dipakai");
      return;
    }
    setReturModal(m => ({ ...m, submitting: true }));
    try {
      const { data } = await api.post("/retur/penjualan", {
        sale_id: returModal.saleId,
        items: dipilih.map(r => ({ index_item: r.index, qty: r.qtyRetur, alasan: r.alasan.trim() })),
        nominal_kembali: returModal.nominal_kembali,
        jenis_kembali: returModal.jenisKembali || "uang",
        metode_kembali: returModal.jenisKembali === "tukar_unit" ? "" : returModal.akun,
        catatan: returModal.catatan || "",
        no_hp_pelanggan: perluHpKredit ? returModal.noHpKredit.trim() : "",
      });
      toast.success(data.message || "Retur penjualan diajukan — menunggu approval");
      setReturModal(null);
      setDetailId(null); setDetail(null);
      // Pemegang retur.create SAJA (tanpa retur.view) tidak bisa membuka
      // /retur — halaman itu digerbang modul "retur" view. Untuk mereka,
      // cukup tutup modal; toast di atas (dari `data.message`) sudah
      // menyebut nomor pengajuan yang menunggu approval.
      if (can("retur", "view")) navigate("/retur");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Gagal mengajukan retur");
      setReturModal(m => ({ ...m, submitting: false }));
    }
  };

  // Cetak faktur/struk (brief #17-19 + pemilih template) — satu alur untuk
  // dua tombol ("Cetak Faktur"/"Cetak Struk"): pilih JENIS dulu lewat tombol
  // mana yang ditekan, lalu (kalau ada lebih dari satu template untuk jenis
  // itu) pilih TEMPLATE-nya. Tombol tidak pernah hilang diam-diam lagi — kalau
  // belum ada template sama sekali, modal menjelaskan itu alih-alih tombolnya
  // disembunyikan begitu saja.
  const bukaCetak = async (jenisCetak) => {
    if (!detail) return;
    setCetakModal({ jenis: jenisCetak, step: "memuat_template", templates: [], templateId: null, loading: true, data: null });
    if (!bolehFaktur) {
      // Tidak punya izin menelusuri daftar template ("faktur.view") — untuk
      // faktur, biarkan backend memilih default jenis "penjualan" seperti
      // biasa. Untuk struk, tidak ada cara menemukan template struk tanpa
      // izin ini, jadi jelaskan alih-alih memaksakan/menyembunyikan tombol.
      if (jenisCetak === "struk") {
        setCetakModal({ jenis: jenisCetak, step: "tanpa_izin", templates: [], loading: false, data: null });
        return;
      }
      await muatDokumenCetak(jenisCetak, null);
      return;
    }
    try {
      const { data } = await api.get("/faktur/templates", { params: { jenis: jenisCetak } });
      const daftar = data?.items || [];
      if (daftar.length === 0) {
        setCetakModal({ jenis: jenisCetak, step: "kosong", templates: [], loading: false, data: null });
        return;
      }
      if (daftar.length === 1) {
        await muatDokumenCetak(jenisCetak, daftar[0]);
        return;
      }
      const def = daftar.find(t => t.is_default) || daftar[0];
      setCetakModal({ jenis: jenisCetak, step: "pilih_template", templates: daftar, templateId: def.id, loading: false, data: null });
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Gagal memuat daftar template cetak");
      setCetakModal(null);
    }
  };

  const muatDokumenCetak = async (jenisCetak, template) => {
    setCetakModal(m => ({ ...(m || { jenis: jenisCetak }), jenis: jenisCetak, step: "preview", loading: true, data: null }));
    try {
      // Struk berbagi satu entitas sale yang sama dengan faktur. Backend
      // /faktur/data/penjualan (lihat faktur.py::_pilih_template +
      // _JENIS_SETARA) sekarang menerima template_id berjenis "struk" untuk
      // dokumen "penjualan" selama dipilih EKSPLISIT seperti di sini, jadi
      // template_id cukup dikirim apa adanya — tidak perlu lagi menimpa
      // `template` di sisi klien seperti sebelumnya.
      const { data } = await api.get(`/faktur/data/penjualan/${detail.id}`, {
        params: template?.id ? { template_id: template.id } : {},
      });
      setCetakModal(m => (m ? { ...m, step: "preview", loading: false, data } : m));
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Gagal memuat dokumen cetak");
      setCetakModal(null);
    }
  };

  const kirimFakturWA = async () => {
    if (!detail) return;
    try {
      const { data } = await api.post(`/faktur/penjualan/${detail.id}/kirim-wa`);
      if (data.ok) {
        toast.success("Faktur terkirim otomatis via WhatsApp");
      } else if (data.wa_link) {
        window.open(data.wa_link, "_blank");
        toast.success("Tautan faktur siap — WhatsApp terbuka");
      } else {
        toast.error("Tautan tidak terbentuk — cek no. HP pelanggan");
      }
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Gagal mengirim faktur via WhatsApp");
    }
  };

  const openDetail = async (id) => {
    setDetailId(id); setDetail(null);
    try {
      const { data } = await api.get(`/sales/${id}`);
      setDetail(data);
    } catch { toast.error("Gagal memuat detail"); setDetailId(null); }
  };

  // Deep-link ke satu transaksi (brief bos 12 Agu #7) — dipanggil dari
  // `?id=` di URL. Diambil LANGSUNG dari API by id (bukan dicari di `items`,
  // yang cuma berisi halaman/filter periode yang sedang aktif), jadi transaksi
  // tetap terbuka walau ada di bulan/cabang lain di luar filter saat ini.
  // Kalau gagal, tampilkan alasan yang jelas lewat `linkedSaleNotice` — beda
  // dari `openDetail` biasa (toast singkat), karena tautan ini sering dibuka
  // sebagai satu-satunya aksi pengguna di halaman ini, dan toast bisa terlewat.
  const openLinkedSale = useCallback(async (id) => {
    setDetailId(id); setDetail(null); setLinkedSaleNotice(null);
    try {
      const { data } = await api.get(`/sales/${id}`);
      setDetail(data);
    } catch (e) {
      setDetailId(null); setDetail(null);
      const status = e?.response?.status;
      setLinkedSaleNotice(
        status === 404
          ? "Transaksi yang Anda tuju tidak ditemukan — kemungkinan sudah dihapus."
          : status === 403
          ? "Anda tidak berhak melihat transaksi ini."
          : (e?.response?.data?.detail || "Gagal memuat transaksi yang dituju. Coba lagi.")
      );
    } finally {
      // Bersihkan `?id=` dari URL setelah dicoba (berhasil maupun gagal) —
      // supaya refresh/tombol Kembali browser tidak mengulang percobaan yang
      // sama, dan supaya menutup lalu membuka modal lain tidak "menyalakan
      // ulang" tautan lama.
      setSearchParams(prev => {
        const next = new URLSearchParams(prev);
        next.delete("id");
        return next;
      }, { replace: true });
    }
  }, [setSearchParams]);

  const linkedSaleId = searchParams.get("id");
  useEffect(() => {
    if (linkedSaleId) openLinkedSale(linkedSaleId);
  }, [linkedSaleId, openLinkedSale]);

  const draftWA = async (sale) => {
    setDraftModal({ loading: true, message: "", wa_url: "", no_hp: sale.no_hp || "", nama_pelanggan: sale.nama_pelanggan || "", sale_id: sale.id });
    try {
      const { data } = await api.post("/ai/draft-wa", { sale_id: sale.id });
      setDraftModal({ loading: false, ...data, sale_id: sale.id });
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Gagal generate draft");
      setDraftModal(null);
    }
  };

  const regenDraft = async () => {
    if (!draftModal?.sale_id) return;
    setDraftModal(m => ({ ...m, loading: true }));
    try {
      const { data } = await api.post("/ai/draft-wa", { sale_id: draftModal.sale_id });
      setDraftModal({ loading: false, ...data, sale_id: draftModal.sale_id });
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Gagal regenerate");
      setDraftModal(m => ({ ...m, loading: false }));
    }
  };

  const copyDraft = async () => {
    if (!draftModal?.message) return;
    try {
      await navigator.clipboard.writeText(draftModal.message);
      toast.success("Draft disalin!");
    } catch { toast.error("Gagal salin"); }
  };

  const exportExcel = async () => {
    const p = new URLSearchParams();
    if (periode) p.set("periode", periode);
    if (branch) p.set("branch", branch);
    const token = localStorage.getItem("hris_token");
    const res = await fetch(`${API}/sales/export?${p}`, { headers: { Authorization: `Bearer ${token}` } });
    const blob = await res.blob();
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url; a.download = `Sales_${periode}.xlsx`;
    document.body.appendChild(a); a.click(); a.remove(); URL.revokeObjectURL(url);
  };

  // ---- Rekap trade-in (transaksi yang mencentang trade-in) ----
  const tradeInParams = () => {
    const p = new URLSearchParams();
    if (dateFrom || dateTo) {
      if (dateFrom) p.set("date_from", dateFrom);
      if (dateTo) p.set("date_to", dateTo);
    } else if (periode) {
      p.set("periode", periode);
    }
    if (branch) p.set("branch", branch);
    return p;
  };

  const loadTradeIn = async () => {
    setLoadingTradeIn(true);
    try {
      const { data } = await api.get(`/sales/trade-in?${tradeInParams()}`);
      setTradeInData(data);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Gagal memuat rekap trade-in");
    } finally {
      setLoadingTradeIn(false);
    }
  };

  const toggleTradeIn = () => {
    const next = !showTradeIn;
    setShowTradeIn(next);
    if (next) loadTradeIn();
  };

  const exportTradeIn = async () => {
    const token = localStorage.getItem("hris_token");
    const res = await fetch(`${API}/sales/trade-in/export?${tradeInParams()}`, {
      headers: { Authorization: `Bearer ${token}` },
    });
    if (!res.ok) { toast.error("Gagal export rekap trade-in"); return; }
    const blob = await res.blob();
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `Rekap_TradeIn_${dateFrom || dateTo ? `${dateFrom || "awal"}_sd_${dateTo || "akhir"}` : periode}.xlsx`;
    document.body.appendChild(a); a.click(); a.remove(); URL.revokeObjectURL(url);
    toast.success("Rekap trade-in sedang diunduh");
  };

  const exportDaily = async () => {
    const p = new URLSearchParams();
    if (periode) p.set("periode", periode);
    if (branch) p.set("branch", branch);
    const token = localStorage.getItem("hris_token");
    const res = await fetch(`${API}/export/sales-daily?${p}`, { headers: { Authorization: `Bearer ${token}` } });
    if (!res.ok) { toast.error("Gagal export"); return; }
    const blob = await res.blob();
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url; a.download = `Sales_Daily_${periode}.xlsx`;
    document.body.appendChild(a); a.click(); a.remove(); URL.revokeObjectURL(url);
    toast.success("Export per-hari (31 sheet) sedang diunduh");
  };

  // B3 (keputusan 23 Sep): header = jumlah NILAI NOTA setelah diskon (nota
  // bertempo dihitung penuh) — angka yang sama dengan kartu-kartu di bawahnya.
  // HANYA header ini; rumus omzet KPI/komisi/dashboard tidak disentuh (D12).
  // Dokumen yang gagal dilampiri nilai nota (data ganjil) jatuh ke rumus lama.
  const totalNilaiPenjualan = items.reduce(
    (a, s) => a + (s.nilai_nota ?? (s.harga_terima || s.total_harga || 0)), 0);

  const branchEmployees = form.branch
    ? employees.filter(e => !e.branch || e.branch === form.branch)
    : employees;

  // Pengaman isian belum tersimpan (brief owner) — form Input/Edit Penjualan
  // adalah form terpanjang di halaman ini (item + trade-in + IMEI, menjurnal +
  // potong stok), dan Ajukan Retur punya qty + alasan per baris. Hook ini
  // dipanggil di level komponen (bukan di dalam JSX kondisional
  // `{modal && ...}`), jadi `active` WAJIB diikat ke flag buka/tutup modalnya
  // sendiri supaya status "dirty" & konfirmasi ikut reset tiap modal dibuka
  // ulang (lihat docstring `active` di hooks/useModalGuard.jsx).
  const saleGuard = useModalGuard({
    onClose: () => setModal(false), onSubmit: save, busy: saving, dangerous: true, active: modal,
  });
  const returGuard = useModalGuard({
    onClose: () => setReturModal(null), onSubmit: submitRetur,
    busy: !!(returModal?.loading || returModal?.submitting), dangerous: true, active: !!returModal,
  });
  // Dialog PPN (hand-rolled) terbuka DI ATAS modal Input Penjualan ini —
  // daftarkan sebagai lapisan lebih atas supaya Escape yang ditujukan untuk
  // dialog PPN tidak ikut memicu palang "ada isian belum disimpan" milik
  // saleGuard di baliknya (lihat useModalGuard.jsx).
  useModalLayer(!!ppnConfirm);

  return (
    <div className="p-4 md:p-8 max-w-[1400px]">
      <header className="flex items-start justify-between mb-6 flex-wrap gap-4">
        <div>
          <p className="text-xs font-semibold uppercase tracking-wider text-emerald-600 mb-2">Penjualan</p>
          <h1 className="font-display text-2xl md:text-3xl font-bold text-slate-900">Input Penjualan</h1>
          <p className="mt-2 text-sm text-slate-500 max-w-2xl">
            <Info className="w-3.5 h-3.5 inline mb-0.5 mr-1 text-slate-400" />
            Klik <b>kartu penjualan</b> untuk melihat detail. Setelah simpan, Anda bisa <b>edit</b> dalam <b>{editWindow} menit</b>.
          </p>
        </div>
        <div className="flex gap-2">
          <button onClick={async () => {
            const token = localStorage.getItem("hris_token");
            const res = await fetch(`${API}/import-templates/sales`, { headers: { Authorization: `Bearer ${token}` } });
            if (!res.ok) { toast.error("Gagal download template"); return; }
            const blob = await res.blob();
            const url = URL.createObjectURL(blob);
            const a = document.createElement("a"); a.href = url; a.download = "Template_Sales.xlsx";
            document.body.appendChild(a); a.click(); a.remove(); URL.revokeObjectURL(url);
          }} data-testid="btn-template-sales"
            className="inline-flex items-center gap-2 h-10 px-4 rounded-lg border border-emerald-200 bg-emerald-50 hover:bg-emerald-100 text-emerald-700 text-sm font-medium">
            <FileText className="w-4 h-4" /> Template
          </button>
          {canEdit && (
            <label data-testid="btn-import-sales"
              className="inline-flex items-center gap-2 h-10 px-4 rounded-lg border border-slate-300 bg-white hover:bg-slate-50 text-slate-700 text-sm font-medium cursor-pointer">
              <Upload className="w-4 h-4" /> Import
              <input type="file" accept=".xlsx,.xls" className="hidden"
                onChange={async (e) => {
                  const f = e.target.files?.[0]; if (!f) return;
                  const fd = new FormData(); fd.append("file", f);
                  try {
                    const { data } = await api.post("/sales/import", fd, { headers: { "Content-Type": "multipart/form-data" } });
                    toast.success(`Import: ${data.created} baru${data.errors.length ? `, ${data.errors.length} error` : ""}`);
                    load();
                  } catch (err) { toast.error(err?.response?.data?.detail || "Gagal import"); }
                  e.target.value = "";
                }} />
            </label>
          )}
          <button onClick={exportExcel} data-testid="btn-export-sales"
            className="inline-flex items-center gap-2 h-10 px-4 rounded-lg border border-slate-300 text-slate-700 hover:bg-slate-50 text-sm font-medium">
            <Download className="w-4 h-4" /> Excel
          </button>
          <button onClick={exportDaily} data-testid="btn-export-sales-daily"
            className="inline-flex items-center gap-2 h-10 px-4 rounded-lg border border-emerald-300 bg-emerald-50 hover:bg-emerald-100 text-emerald-800 text-sm font-medium">
            <Download className="w-4 h-4" /> Export Harian (Per Sheet)
          </button>
          <button onClick={toggleTradeIn} data-testid="btn-toggle-tradein-recap"
            className={`inline-flex items-center gap-2 h-10 px-4 rounded-lg border text-sm font-medium transition-colors ${
              showTradeIn
                ? "border-violet-400 bg-violet-100 text-violet-900"
                : "border-violet-300 bg-violet-50 hover:bg-violet-100 text-violet-800"
            }`}>
            <Repeat className="w-4 h-4" /> Rekap Trade-in
          </button>
          {bolehKelolaMetode(user) && (
            <TombolKelolaMetode jumlah={paymentMethods.length} onClick={() => setMethodsModal(true)} />
          )}
          {canEdit && (
            <button onClick={openCreate} data-testid="btn-add-sale"
              className="hidden">+</button>
          )}
        </div>
      </header>

      {methodsModal && (
        <MetodePembayaranManager
          groups={paymentGroupsAll}
          onClose={() => setMethodsModal(false)}
          onChange={loadMethods}
        />
      )}

      <div className="bg-white border border-slate-200 rounded-xl p-4 shadow-sm mb-6 flex flex-wrap items-end gap-3">
        <label className="block">
          <span className="text-xs font-medium text-slate-600">Periode Bulan</span>
          <MonthPicker value={periode} onChange={v => { setPeriode(v); setDateFrom(""); setDateTo(""); }}
            data-testid="sales-periode"
            className="mt-1" />
        </label>
        <label className="block">
          <span className="text-xs font-medium text-slate-600">Dari Tanggal</span>
          <DatePicker value={dateFrom} onChange={setDateFrom}
            data-testid="sales-date-from"
            className="mt-1" />
        </label>
        <label className="block">
          <span className="text-xs font-medium text-slate-600">Sampai Tanggal</span>
          <DatePicker value={dateTo} onChange={setDateTo}
            data-testid="sales-date-to"
            className="mt-1" />
        </label>
        {(dateFrom || dateTo) && (
          <button onClick={() => { setDateFrom(""); setDateTo(""); }}
            data-testid="sales-reset-range"
            className="h-10 px-3 rounded-lg border border-slate-200 text-xs text-slate-600 hover:bg-slate-50">
            Reset Range
          </button>
        )}
        <label className="block">
          <span className="text-xs font-medium text-slate-600">Branch</span>
          <select value={branch} onChange={e => setBranch(e.target.value)}
            className="mt-1 h-10 px-3 rounded-lg border border-slate-200 text-sm bg-white min-w-[180px]">
            {BRANCHES.map(b => <option key={b} value={b}>{b || "Semua Branch"}</option>)}
          </select>
        </label>
        <div className="ml-auto flex gap-6 text-right">
          <div>
            <p className="text-xs text-slate-500">Transaksi</p>
            <p className="font-display text-2xl font-bold text-slate-900">{items.length}</p>
          </div>
          <div>
            <p className="text-xs text-slate-500">Nilai Penjualan (setelah diskon)</p>
            <p className="font-display text-2xl font-bold text-emerald-700" data-testid="sales-header-nilai">{idr(totalNilaiPenjualan)}</p>
          </div>
        </div>
      </div>

      {showTradeIn && (
        <div className="bg-white border border-violet-200 rounded-xl shadow-sm mb-6 overflow-hidden"
          data-testid="tradein-recap-panel">
          <div className="flex flex-wrap items-center justify-between gap-3 px-4 py-3 border-b border-violet-100 bg-violet-50/60">
            <div>
              <p className="font-display font-semibold text-slate-900 inline-flex items-center gap-2">
                <Repeat className="w-4 h-4 text-violet-700" /> Rekap Transaksi Trade-in
              </p>
              <p className="text-xs text-slate-500 mt-0.5">Mengikuti filter periode &amp; branch di atas.</p>
            </div>
            <button onClick={exportTradeIn} data-testid="btn-export-tradein"
              className="inline-flex items-center gap-2 h-9 px-3 rounded-lg bg-violet-600 hover:bg-violet-700 text-white text-sm font-medium transition-colors">
              <Download className="w-4 h-4" /> Export Excel
            </button>
          </div>

          <LoadingState
            loading={loadingTradeIn}
            kosong={!loadingTradeIn && (!tradeInData || tradeInData.rows.length === 0)}
            pesanKosong="Tidak ada transaksi trade-in pada filter ini."
            data-testid="tradein-recap-empty"
          >
            {tradeInData && (
              <>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-px bg-slate-100">
                {[
                  ["Transaksi", tradeInData.summary.n_transaksi, "text-slate-900"],
                  ["Unit Trade-in", tradeInData.summary.n_unit_trade_in, "text-violet-700"],
                  ["Nilai Trade-in", idr(tradeInData.summary.total_nilai_trade_in), "text-violet-700"],
                  ["Omzet Transaksi", idr(tradeInData.summary.total_omzet), "text-emerald-700"],
                ].map(([label, value, tone]) => (
                  <div key={label} className="bg-white px-4 py-3">
                    <p className="text-xs text-slate-500">{label}</p>
                    <p className={`font-display text-lg font-bold ${tone}`}>{value}</p>
                  </div>
                ))}
              </div>

              {tradeInData.summary.unit_tanpa_imei > 0 && (
                <p className="px-4 py-2 text-xs text-amber-800 bg-amber-50 border-b border-amber-200"
                  data-testid="tradein-missing-imei-warning">
                  {tradeInData.summary.unit_tanpa_imei} unit trade-in belum punya IMEI. Lengkapi lewat menu Input Nota agar mudah dilacak.
                </p>
              )}

              <TabelDesktop>
                <table className="w-full text-sm">
                  <thead>
                    <tr className="bg-slate-50 text-xs font-semibold text-slate-500 uppercase tracking-wider">
                      <SortTh label="Tanggal" sortKeyName="tanggal" sortKey={tradeInSort.sortKey} sortDir={tradeInSort.sortDir} onSort={tradeInSort.toggle} testid="tradein-sort-tanggal" />
                      <SortTh label="Pelanggan" sortKeyName="nama_pelanggan" sortKey={tradeInSort.sortKey} sortDir={tradeInSort.sortDir} onSort={tradeInSort.toggle} testid="tradein-sort-pelanggan" />
                      <th className="text-left py-2.5 px-4">Barang Dijual</th>
                      <th className="text-left py-2.5 px-4">Unit Trade-in &amp; IMEI</th>
                      <SortTh label="Nilai Trade-in" sortKeyName="nilai_trade_in" sortKey={tradeInSort.sortKey} sortDir={tradeInSort.sortDir} onSort={tradeInSort.toggle} align="right" testid="tradein-sort-nilai" />
                      <SortTh label="Penjual" sortKeyName="nama_penjual" sortKey={tradeInSort.sortKey} sortDir={tradeInSort.sortDir} onSort={tradeInSort.toggle} testid="tradein-sort-penjual" />
                    </tr>
                  </thead>
                  <tbody>
                    {sortRows(tradeInData.rows, tradeInSort.sortKey, tradeInSort.sortDir).map(r => (
                      <tr key={r.sale_id} className="border-t border-slate-100 hover:bg-slate-50"
                        data-testid={`tradein-row-${r.sale_id}`}>
                        <td className="py-2.5 px-4 text-xs text-slate-700 whitespace-nowrap">
                          {r.tanggal}<span className="block text-[10px] text-slate-400">{r.waktu}</span>
                        </td>
                        <td className="py-2.5 px-4 text-xs text-slate-900 font-medium">{r.nama_pelanggan || "-"}</td>
                        <td className="py-2.5 px-4 text-xs text-slate-700">{r.barang_dijual || "-"}</td>
                        <td className="py-2.5 px-4">
                          {r.trade_in_units.length === 0 ? (
                            <span className="text-xs italic text-slate-400">tanpa rincian</span>
                          ) : r.trade_in_units.map((u, i) => (
                            <div key={i} className="text-xs text-slate-800">
                              {u.nama || <span className="italic text-slate-400">tanpa nama</span>}
                              {u.imei
                                ? <span className="ml-1 font-mono text-[10px] text-violet-700">{u.imei}</span>
                                : <span className="ml-1 text-[10px] text-amber-600">IMEI kosong</span>}
                            </div>
                          ))}
                        </td>
                        <td className="py-2.5 px-4 text-right font-mono text-xs font-semibold text-violet-800">
                          {idr(r.nilai_trade_in)}
                        </td>
                        <td className="py-2.5 px-4 text-xs text-slate-700">{r.nama_penjual || "-"}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </TabelDesktop>

              <DaftarKartuMobile testid="tradein-recap-kartu">
                {sortRows(tradeInData.rows, tradeInSort.sortKey, tradeInSort.sortDir).map(r => (
                  <KartuBaris key={r.sale_id} testid={`tradein-row-${r.sale_id}-m`}>
                    <KartuBarisHeader
                      kiri={<>
                        <p className="text-sm font-semibold text-slate-900">{r.nama_pelanggan || "-"}</p>
                        <p className="text-xs text-slate-500 mt-0.5">{r.tanggal} · {r.waktu}</p>
                      </>}
                      kanan={<span className="font-mono text-sm font-semibold text-violet-800">{idr(r.nilai_trade_in)}</span>}
                    />
                    <KartuBarisMeta label="Barang Dijual" value={r.barang_dijual || "-"} />
                    <KartuBarisMeta label="Penjual" value={r.nama_penjual || "-"} />
                    <div className="text-xs">
                      <span className="text-slate-500">Unit Trade-in &amp; IMEI</span>
                      {r.trade_in_units.length === 0 ? (
                        <p className="italic text-slate-400 mt-0.5">tanpa rincian</p>
                      ) : r.trade_in_units.map((u, i) => (
                        <div key={i} className="text-slate-800 mt-0.5">
                          {u.nama || <span className="italic text-slate-400">tanpa nama</span>}
                          {u.imei
                            ? <span className="ml-1 font-mono text-[10px] text-violet-700">{u.imei}</span>
                            : <span className="ml-1 text-[10px] text-amber-600">IMEI kosong</span>}
                        </div>
                      ))}
                    </div>
                  </KartuBaris>
                ))}
              </DaftarKartuMobile>
              </>
            )}
          </LoadingState>
        </div>
      )}

      {items.length === 0 ? (
        <div className="bg-white border border-slate-200 rounded-xl p-16 text-center">
          <MessageCircle className="w-12 h-12 text-slate-300 mx-auto mb-3" />
          <p className="font-display font-semibold text-slate-900">Belum ada transaksi di periode ini</p>
          <p className="text-sm text-slate-500 mt-1">Klik &quot;Input Penjualan Baru&quot; untuk mulai mencatat.</p>
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {items.map(s => {
            const editable = canEditNow(s.created_at, editWindow);
            const editCount = s.edit_count || 0;
            return (
              <button
                type="button"
                key={s.id}
                data-testid={`sale-${s.id}`}
                onClick={() => openDetail(s.id)}
                className="text-left bg-white border border-slate-200 hover:border-emerald-400 hover:shadow-md transition-all rounded-xl p-5 shadow-sm relative group focus:outline-none focus:ring-2 focus:ring-emerald-500/40"
              >
                <div className="flex items-start justify-between mb-3">
                  <div>
                    <p className="font-display font-semibold text-slate-900 group-hover:text-emerald-700">{s.nama_pelanggan}</p>
                    <p className="text-xs text-slate-500 flex items-center gap-1 mt-0.5">
                      <Phone className="w-3 h-3" /> {s.no_hp || "-"}
                    </p>
                  </div>
                  <div className="text-right">
                    <p className="text-xs text-slate-500 font-mono">{fmtDateTime(s.tanggal, s.waktu)}</p>
                    <p className="text-xs text-emerald-700 font-medium">{s.branch || "-"}</p>
                  </div>
                </div>
                <div className="space-y-1 mb-3 pb-3 border-b border-slate-100">
                  {s.items?.slice(0, 3).map((it, i) => (
                    <div key={i} className="flex justify-between text-sm">
                      <span className="text-slate-700 truncate mr-2">{it.nama_barang}</span>
                      <span className="font-mono text-slate-900 whitespace-nowrap">{idr(it.harga)}</span>
                    </div>
                  ))}
                  {s.items?.length > 3 && (
                    <p className="text-xs text-slate-400">+{s.items.length - 3} barang lain</p>
                  )}
                </div>
                <div className="flex items-center gap-1.5 flex-wrap text-xs mb-3">
                  {s.tempo_pembayaran && !((s.dibayar ?? s.uang_diterima ?? 0) > 0) ? (
                    // B3: nota bertempo yang belum dibayar sepeser pun TIDAK boleh
                    // tampak "lunas via Transfer BCA" (kasus gambar owner).
                    <span className="px-2 py-0.5 rounded-full bg-amber-100 text-amber-800 inline-flex items-center gap-1"
                      data-testid={`sale-belum-bayar-${s.id}`}>
                      <Clock className="w-3 h-3" /> Tempo — belum dibayar
                    </span>
                  ) : s.payments && s.payments.length > 1 ? (
                    <span className="px-2 py-0.5 rounded-full bg-sky-100 text-sky-800 inline-flex items-center gap-1"
                      data-testid={`sale-split-${s.id}`}>
                      <Split className="w-3 h-3" /> Split ({s.payments.length})
                    </span>
                  ) : (
                    <span className="px-2 py-0.5 rounded-full bg-slate-100 text-slate-700 inline-flex items-center gap-1">
                      <CreditCard className="w-3 h-3" /> {s.metode_pembayaran}
                    </span>
                  )}
                  {s.trade_in && (
                    <span className="px-2 py-0.5 rounded-full bg-amber-100 text-amber-800 inline-flex items-center gap-1">
                      <Repeat className="w-3 h-3" /> Trade-in
                    </span>
                  )}
                  {s.n_items > 1 && (
                    <span className="px-2 py-0.5 rounded-full bg-emerald-100 text-emerald-800">
                      {s.n_items} item
                    </span>
                  )}
                  {editCount > 0 && (
                    <span className="px-2 py-0.5 rounded-full bg-violet-100 text-violet-800 inline-flex items-center gap-1"
                      data-testid={`sale-editmark-${s.id}`} title={`Sudah diedit ${editCount} kali`}>
                      <Pencil className="w-3 h-3" /> {editCount}×
                    </span>
                  )}
                </div>
                <div className="flex items-end justify-between pt-2">
                  <div>
                    <p className="text-xs text-slate-500">Penjual</p>
                    <p className="text-sm font-medium text-slate-900">{s.nama_penjual}</p>
                  </div>
                  {/* B3 (keputusan 23 Sep): angka besar = NILAI NOTA (barang −
                      diskon + admin + PPN) — sama untuk tunai, bertempo, dan
                      trade-in. Di bawahnya uang yang sudah diterima, dan untuk
                      nota bertempo sisa tagihan dari DOKUMEN PIUTANG (pembayaran
                      di Finance tidak memperbarui nota). */}
                  <div className="text-right">
                    <p className="font-display font-bold text-emerald-700" data-testid={`sale-nilai-${s.id}`}>
                      {idr(s.nilai_nota ?? (s.harga_terima || s.total_harga))}
                    </p>
                    {(s.diskon_tampil || 0) > 0 && (
                      <p className="text-[11px] text-rose-700">diskon −{idr(s.diskon_tampil)}</p>
                    )}
                    {/* "Dibayar" = uang + DP + trade-in (cara bayar, D5) + cicilan
                        piutang di Finance — angka server `dibayar`. */}
                    {(s.dibayar ?? s.uang_diterima) != null && (
                      <p className="text-[11px] text-slate-500" data-testid={`sale-dibayar-${s.id}`}>Dibayar {idr(s.dibayar ?? s.uang_diterima)}</p>
                    )}
                    {s.tempo_pembayaran && (
                      <p className="text-[11px] text-amber-700" data-testid={`sale-tempo-${s.id}`}>
                        Tempo {labelTempo(s.tempo_pembayaran)}
                        {s.tanggal_jatuh_tempo ? ` · jatuh tempo ${formatTanggal(s.tanggal_jatuh_tempo)}` : ""}
                        {s.sisa_tempo != null ? ` · sisa ${idr(s.sisa_tempo)}` : ""}
                      </p>
                    )}
                  </div>
                </div>
                {/* Quick action: Draft Balasan AI.
                    Gerbang fitur AI (brief owner 30 Agu 2026) — kunci
                    `ai_assistant`, sama dengan backend /ai/draft-wa. */}
                {bolehPakaiAI(user, can) && (
                <div className="mt-3 pt-2 border-t border-slate-100">
                  <button
                    onClick={(e) => { e.stopPropagation(); draftWA(s); }}
                    data-testid={`sale-draft-wa-${s.id}`}
                    className="w-full inline-flex items-center justify-center gap-1.5 h-8 px-3 rounded-lg border border-emerald-200 bg-emerald-50/60 hover:bg-emerald-100 text-emerald-700 text-xs font-medium">
                    <Sparkles className="w-3.5 h-3.5" /> Draft Balasan AI
                  </button>
                </div>
                )}
                {/* Corner edit / lock icon (informational, clickable via detail) */}
                {canEdit && (
                  <div className="absolute top-3 right-3 flex gap-1 items-center">
                    {editable ? (
                      <span data-testid={`sale-editable-${s.id}`}
                        className="text-[10px] text-emerald-700 bg-emerald-50 border border-emerald-200 rounded-full px-2 py-0.5 inline-flex items-center gap-1">
                        <Clock className="w-3 h-3" /> {minutesLeft(s.created_at, editWindow)}m
                      </span>
                    ) : (
                      <span data-testid={`sale-locked-${s.id}`}
                        className="text-[10px] text-slate-400 bg-slate-50 border border-slate-200 rounded-full px-2 py-0.5 inline-flex items-center gap-1">
                        <Lock className="w-3 h-3" /> Terkunci
                      </span>
                    )}
                  </div>
                )}
              </button>
            );
          })}
        </div>
      )}

      {/* Deep-link gagal dibuka (brief bos 12 Agu #7) — tautan dari Pelanggan.jsx
          yang menunjuk transaksi terhapus/tak berhak/error, supaya pengguna
          tidak mendarat di halaman kosong tanpa penjelasan. */}
      {linkedSaleNotice && (
        <div className="fixed inset-0 z-50 bg-black/50 backdrop-blur-sm grid place-items-center p-4"
          onClick={() => setLinkedSaleNotice(null)}>
          <div className="bg-white rounded-2xl w-full max-w-md p-6" onClick={e => e.stopPropagation()}
            data-testid="sale-linked-notfound">
            <div className="flex items-start gap-3 mb-4">
              <div className="w-10 h-10 rounded-full bg-rose-50 border border-rose-200 flex items-center justify-center flex-shrink-0">
                <AlertTriangle className="w-5 h-5 text-rose-600" />
              </div>
              <div>
                <h3 className="font-display font-semibold text-slate-900">Transaksi Tidak Bisa Dibuka</h3>
                <p className="text-sm text-slate-600 mt-1">{linkedSaleNotice}</p>
              </div>
            </div>
            <button onClick={() => setLinkedSaleNotice(null)} data-testid="sale-linked-notfound-close"
              className="w-full h-10 rounded-lg bg-slate-900 hover:bg-slate-800 text-white text-sm font-medium">
              Tutup
            </button>
          </div>
        </div>
      )}

      {/* DETAIL MODAL */}
      {detailId && (
        <div className="fixed inset-0 z-50 bg-black/50 backdrop-blur-sm grid place-items-center p-4 overflow-y-auto"
          onClick={() => { setDetailId(null); setDetail(null); }}>
          <div className="bg-white rounded-2xl w-full max-w-2xl my-8" onClick={e => e.stopPropagation()}
            data-testid="sale-detail-modal">
            <div className="sticky top-0 bg-slate-900 text-white px-6 py-4 flex items-center justify-between rounded-t-2xl z-10">
              <div className="flex items-center gap-2">
                <FileText className="w-5 h-5" />
                <h3 className="font-display font-semibold">Detail Penjualan</h3>
              </div>
              <button onClick={() => { setDetailId(null); setDetail(null); }}
                className="p-2 hover:bg-white/20 rounded-lg" data-testid="sale-detail-close">
                <X className="w-4 h-4" />
              </button>
            </div>
            {!detail ? (
              <div className="p-6">
                <LoadingState loading jumlahKartu={1} data-testid="sale-detail-loading" />
              </div>
            ) : (
              <div className="p-6 space-y-5">
                {/* Header */}
                <div className="flex items-start justify-between border-b border-slate-100 pb-4">
                  <div>
                    <p className="text-xs text-slate-500 uppercase tracking-wider">Pelanggan</p>
                    <p className="font-display text-lg font-bold text-slate-900">{detail.nama_pelanggan}</p>
                    <p className="text-sm text-slate-500 mt-1"><Phone className="w-3.5 h-3.5 inline mr-1" />{detail.no_hp || "-"}</p>
                  </div>
                  <div className="text-right">
                    <p className="text-xs text-slate-500 uppercase tracking-wider">Waktu</p>
                    <p className="font-mono text-sm text-slate-900">{fmtDateTime(detail.tanggal, detail.waktu)}</p>
                    <p className="text-xs text-emerald-700 font-medium mt-1">{detail.branch || "-"}</p>
                  </div>
                </div>

                {/* Items — diskon PRODUK per baris (D5) tampil di barisnya, dan
                    footer merinci subtotal → diskon → admin → PPN → NILAI NOTA
                    (B3, angka server `nilai_nota`). */}
                <div>
                  <p className="text-xs font-semibold uppercase tracking-wider text-slate-500 mb-2">Barang Dibeli</p>
                  <div className="border border-slate-200 rounded-lg overflow-hidden overflow-x-auto">
                    <table className="w-full text-sm">
                      <thead className="bg-slate-50 text-xs text-slate-500">
                        <tr>
                          <th className="text-left py-2 px-3">Nama Barang</th>
                          <th className="text-left py-2 px-3">Kode</th>
                          <th className="text-right py-2 px-3">Harga</th>
                        </tr>
                      </thead>
                      <tbody>
                        {(detail.items || []).map((it, i) => {
                          const d = Number(it.diskon) || 0;
                          return (
                          <tr key={i} className="border-t border-slate-100" data-testid={`sale-detail-item-${i}`}>
                            <td className="py-2 px-3 font-medium text-slate-900">
                              {it.nama_barang}
                              {it.is_bonus && (
                                <span className="ml-1.5 px-1.5 py-0.5 rounded-full bg-amber-100 text-amber-800 text-[10px] font-semibold align-middle">Bonus</span>
                              )}
                            </td>
                            <td className="py-2 px-3"><span className="px-2 py-0.5 rounded-full bg-slate-100 text-slate-700 text-xs">{it.kode || "HP"}</span></td>
                            <td className="py-2 px-3 text-right font-mono">
                              {idr(it.harga)}
                              {d > 0 && (
                                <span className="block text-[11px] text-rose-700" data-testid={`sale-detail-item-diskon-${i}`}>
                                  diskon −{idr(d)} · bersih {idr((Number(it.harga) || 0) - d)}
                                </span>
                              )}
                            </td>
                          </tr>
                          );
                        })}
                      </tbody>
                      <tfoot className="bg-slate-50 text-xs">
                        <tr>
                          <td colSpan={2} className="py-1.5 px-3 text-right text-slate-500">Subtotal barang</td>
                          <td className="py-1.5 px-3 text-right font-mono">{idr(detail.total_harga)}</td>
                        </tr>
                        {(detail.diskon_tampil || 0) > 0 && (
                          <tr>
                            <td colSpan={2} className="py-1 px-3 text-right text-rose-700">
                              Diskon{(detail.items || []).some(it => (Number(it.diskon) || 0) > 0) ? " produk" : ""}
                              {detail.diskon_pic_approval ? ` (PIC ${detail.diskon_pic_approval})` : ""}
                            </td>
                            <td className="py-1 px-3 text-right font-mono text-rose-700" data-testid="sale-detail-diskon">−{idr(detail.diskon_tampil)}</td>
                          </tr>
                        )}
                        {(detail.biaya_admin_rp || 0) > 0 && (
                          <tr>
                            <td colSpan={2} className="py-1 px-3 text-right text-emerald-700">Biaya Admin</td>
                            <td className="py-1 px-3 text-right font-mono text-emerald-700">+{idr(detail.biaya_admin_rp)}</td>
                          </tr>
                        )}
                        {/* PPN (keputusan owner 18 Agu 2026) — DPP & PPN
                            ditampilkan terpisah, angkanya dari server (bukan
                            pratinjau klien), termasuk tarif yang tersimpan di
                            dokumen (bisa beda dari tarif berlaku saat ini). */}
                        {detail.kena_ppn && (
                          <>
                            <tr data-testid="detail-dpp">
                              <td colSpan={2} className="py-1 px-3 text-right text-slate-600">DPP</td>
                              <td className="py-1 px-3 text-right font-mono">{idr(detail.dpp)}</td>
                            </tr>
                            <tr data-testid="detail-ppn-nominal">
                              <td colSpan={2} className="py-1 px-3 text-right text-emerald-700">PPN ({detail.ppn_persen ?? 11}%)</td>
                              <td className="py-1 px-3 text-right font-mono text-emerald-700">+{idr(detail.ppn_nominal)}</td>
                            </tr>
                          </>
                        )}
                        <tr className="border-t border-slate-200 text-sm">
                          <td colSpan={2} className="py-2 px-3 text-right text-slate-700 font-semibold">Nilai Nota</td>
                          <td className="py-2 px-3 text-right font-mono font-bold" data-testid="sale-detail-nilai-nota">
                            {idr(detail.nilai_nota ?? detail.total_harga)}
                          </td>
                        </tr>
                      </tfoot>
                    </table>
                  </div>
                </div>

                {/* Pembayaran (B3/D5/D7): baris metode (+ keterangan/No. Kontrak),
                    lalu trade-in & DP sebagai cara bayar, lalu "Dibayar" (uang
                    diterima) dan — untuk nota bertempo — tempo, jatuh tempo, dan
                    sisa dari DOKUMEN PIUTANG. Nota bertempo yang belum dibayar
                    tidak lagi tampil "lunas via Transfer BCA" (kasus gambar owner). */}
                <div>
                  <p className="text-xs font-semibold uppercase tracking-wider text-slate-500 mb-2">Pembayaran</p>
                  <div className="bg-slate-50 border border-slate-200 rounded-lg p-3 text-sm space-y-1" data-testid="sale-detail-pembayaran">
                    {(detail.payments || []).filter(p => (Number(p.amount) || 0) > 0).length > 0 ? (
                      (detail.payments || []).filter(p => (Number(p.amount) || 0) > 0).map((p, i) => (
                        <div key={i} className="flex justify-between gap-2">
                          <span className="text-slate-700">
                            · {p.method}
                            {p.keterangan && <span className="block text-[11px] text-slate-500 pl-3" data-testid={`sale-detail-ket-${i}`}>{p.keterangan}</span>}
                          </span>
                          <span className="font-mono">{idr(p.amount)}</span>
                        </div>
                      ))
                    ) : (detail.jumlah_pembayaran || 0) > 0 ? (
                      <div className="flex justify-between">
                        <span className="text-slate-700 inline-flex items-center gap-1.5"><CreditCard className="w-4 h-4" />{detail.metode_pembayaran}</span>
                        <span className="font-mono font-semibold">{idr(detail.jumlah_pembayaran)}</span>
                      </div>
                    ) : (
                      <p className="text-xs text-slate-500">Belum ada pembayaran lewat metode.</p>
                    )}
                    {detail.trade_in && (detail.trade_in_items || []).length > 0 ? (
                      detail.trade_in_items.map((t, i) => (
                        <div key={`ti-${i}`} className="flex justify-between gap-2 text-violet-800">
                          <span>· Trade-in: {t.nama || t.nama_barang}{t.imei ? ` (IMEI ${t.imei})` : ""}</span>
                          <span className="font-mono">{idr(t.harga)}</span>
                        </div>
                      ))
                    ) : detail.trade_in ? (
                      <div className="flex items-center gap-2 text-xs text-amber-800">
                        <Repeat className="w-3.5 h-3.5" /> <b>Trade-in:</b> {detail.trade_in_note || "-"}
                      </div>
                    ) : null}
                    {(detail.dp_nominal || 0) > 0 && (
                      <div className="flex justify-between gap-2 text-emerald-800" data-testid="sale-detail-dp">
                        <span>· Downpayment (DP)</span>
                        <span className="font-mono">{idr(detail.dp_nominal)}</span>
                      </div>
                    )}
                    {(detail.terbayar_piutang || 0) > 0 && (
                      <div className="flex justify-between gap-2 text-amber-800" data-testid="sale-detail-terbayar-piutang">
                        <span>· Pembayaran piutang (Finance)</span>
                        <span className="font-mono">{idr(detail.terbayar_piutang)}</span>
                      </div>
                    )}
                    {(detail.dibayar ?? detail.uang_diterima) != null && (
                      <div className="border-t border-slate-200 pt-1 mt-1 flex justify-between font-semibold">
                        <span>Dibayar</span>
                        <span className="font-mono" data-testid="sale-detail-dibayar">{idr(detail.dibayar ?? detail.uang_diterima)}</span>
                      </div>
                    )}
                    {detail.tempo_pembayaran && (
                      <p className="text-xs text-amber-800" data-testid="sale-detail-tempo">
                        Tempo {labelTempo(detail.tempo_pembayaran)}
                        {detail.tanggal_jatuh_tempo ? ` · jatuh tempo ${formatTanggal(detail.tanggal_jatuh_tempo)}` : ""}
                        {detail.sisa_tempo != null ? ` · sisa ${idr(detail.sisa_tempo)}` : " · piutang belum terbit"}
                        {detail.kode_piutang ? ` (${detail.kode_piutang})` : ""}
                      </p>
                    )}
                    {detail.total_pembayaran_kasir != null && (
                      <p className="text-[11px] text-slate-500">
                        Total Pembayaran dikonfirmasi kasir: <span className="font-mono">{idr(detail.total_pembayaran_kasir)}</span>
                      </p>
                    )}
                  </div>
                </div>

                {/* Meta */}
                <div className="grid grid-cols-2 gap-3 text-sm">
                  <Field label="IMEI"><span className="font-mono">{detail.imei || "-"}</span></Field>
                  <Field label="Penjual">{detail.nama_penjual}</Field>
                  <Field label="Catatan"><span className="text-slate-600 italic">{detail.catatan || "-"}</span></Field>
                  <Field label="Input oleh"><span className="text-xs text-slate-600">{detail.created_by}</span></Field>
                </div>

                {/* Edit history */}
                {(detail.edit_count || 0) > 0 && (
                  <div className="bg-violet-50 border border-violet-200 rounded-lg p-3">
                    <p className="text-xs font-semibold text-violet-800 flex items-center gap-1.5 mb-1">
                      <History className="w-3.5 h-3.5" /> Riwayat Edit ({detail.edit_count}×)
                    </p>
                    <ul className="text-xs text-violet-900/80 space-y-0.5">
                      {(detail.edit_history || []).map((h, i) => (
                        <li key={i}>· {formatTanggal(h.at, { denganJam: true })} — {h.by}</li>
                      ))}
                    </ul>
                  </div>
                )}

                {/* Actions */}
                <div className="flex justify-between items-center pt-2 border-t border-slate-100 flex-wrap gap-2">
                  <div className="text-xs text-slate-500">
                    {/* Nota bertempo TIDAK punya jendela 60 menit (F5) — menampilkan
                        hitung mundurnya membuat label berbohong: "Bisa diedit ~0
                        menit lagi" pada nota yang justru boleh diedit selamanya,
                        atau "Waktu edit sudah lewat" pada nota yang tombol Editnya
                        tetap ada. Ketiga keadaan dipisah eksplisit. */}
                    {detail._terkunci_validasi ? (
                      // B1 (brief 22 Sep): piutang Valid mengunci notanya —
                      // server menolak edit 409, jadi dijelaskan di sini.
                      <span className="inline-flex items-center gap-1 text-amber-700" data-testid="sale-detail-terkunci-validasi"><Lock className="w-3.5 h-3.5" /> Piutangnya sudah divalidasi Finance — ajukan Batal Validasi di menu Piutang Usaha untuk mengedit</span>
                    ) : detail._boleh_edit_tempo ? (
                      <span className="inline-flex items-center gap-1 text-emerald-700"><Pencil className="w-3.5 h-3.5" /> Nota bertempo — bisa diedit SPV berwenang, tanpa batas waktu</span>
                    ) : detail._tempo_terkunci_pembayaran ? (
                      <span className="inline-flex items-center gap-1 text-amber-700"><Lock className="w-3.5 h-3.5" /> Piutangnya sudah dibayar — koreksi lewat Edit Pembayaran di Finance</span>
                    ) : detail._can_edit ? (
                      <span className="inline-flex items-center gap-1 text-emerald-700"><Clock className="w-3.5 h-3.5" /> Bisa diedit ~{minutesLeft(detail.created_at, detail._edit_window_minutes)} menit lagi</span>
                    ) : (
                      <span className="inline-flex items-center gap-1 text-slate-500"><Lock className="w-3.5 h-3.5" /> Waktu edit sudah lewat ({detail._edit_window_minutes}m)</span>
                    )}
                  </div>
                  <div className="flex gap-2 flex-wrap">
                    {/* Cetak faktur/struk (brief #17-19) — dua tombol pilih JENIS dokumen,
                        pemilihan TEMPLATE-nya (kalau ada lebih dari satu) terjadi di dalam
                        modal cetak (lihat bukaCetak()). Kedua tombol SELALU tampil — tidak
                        lagi hilang diam-diam kalau template belum dibuat. */}
                    <button data-testid="btn-cetak-faktur" onClick={() => bukaCetak("penjualan")}
                      className="h-10 px-4 rounded-lg border border-slate-300 text-slate-700 hover:bg-slate-50 text-sm font-medium inline-flex items-center gap-1.5">
                      <Printer className="w-4 h-4" /> Cetak Faktur
                    </button>
                    <button data-testid="btn-cetak-struk" onClick={() => bukaCetak("struk")}
                      className="h-10 px-4 rounded-lg border border-slate-300 text-slate-700 hover:bg-slate-50 text-sm font-medium inline-flex items-center gap-1.5">
                      <Printer className="w-4 h-4" /> Cetak Struk
                    </button>
                    {can("sales", "create") && (
                      <button data-testid="btn-kirim-faktur-wa" onClick={kirimFakturWA}
                        className="h-10 px-4 rounded-lg border border-emerald-300 text-emerald-700 hover:bg-emerald-50 text-sm font-medium inline-flex items-center gap-1.5">
                        <Send className="w-4 h-4" /> Kirim Faktur WA
                      </button>
                    )}
                    {/* CSAT: buat tautan penilaian lalu buka WhatsApp dengan teks siap kirim.
                        Idempoten di backend — klik dua kali menghasilkan tautan yang sama. */}
                    <button data-testid="sale-detail-csat"
                      onClick={async () => {
                        try {
                          const r = await api.post("/csat/generate", { sale_id: detail.id });
                          const link = r.data.wa_link;
                          if (link) { window.open(link, "_blank"); toast.success("Tautan CSAT siap — WhatsApp terbuka"); }
                          else toast.error("Tautan tidak terbentuk — cek no. HP pelanggan");
                        } catch (e) { toast.error(e?.response?.data?.detail || "Gagal membuat tautan CSAT"); }
                      }}
                      className="h-10 px-4 rounded-lg border border-emerald-300 text-emerald-700 hover:bg-emerald-50 text-sm font-medium inline-flex items-center gap-1.5">
                      <Send className="w-4 h-4" /> Kirim CSAT
                    </button>
                    {/* Ajukan retur (brief bos) — hanya dokumen, jurnal/stok bergerak
                        saat disetujui SPV/admin di halaman /retur. */}
                    {can("retur", "create") && (
                      <button onClick={() => openReturModal(detail)} data-testid={`btn-retur-${detail.id}`}
                        className="h-10 px-4 rounded-lg border border-rose-300 text-rose-700 hover:bg-rose-50 text-sm font-medium inline-flex items-center gap-1.5">
                        <Undo2 className="w-4 h-4" /> Retur
                      </button>
                    )}
                    {/* Retur SUDAH DISETUJUI mengunci Hapus & Edit (owner, 16 Agu 2026) — returnya
                        sudah membalikkan uang lewat jurnalnya sendiri, jadi menghapus/mengubah
                        penjualannya sekarang akan membalik kas & pendapatan untuk kedua kalinya.
                        Ditampilkan sebagai penjelasan, BUKAN tombol yang ujungnya 409 (aturan #3
                        CLAUDE.md) — backend (`_retur_penjualan_disetujui` di routers/sales.py)
                        tetap menolak permintaan ini walau tombolnya sengaja dipaksa tampil. */}
                    {detail.has_retur_disetujui ? (
                      <span data-testid="sale-detail-locked-retur"
                        title={`Retur ${detail.retur_disetujui_code || ""} sudah disetujui — koreksi lewat menu Retur Penjualan`}
                        className="h-10 px-4 rounded-lg border border-amber-200 bg-amber-50 text-amber-700 text-sm font-medium inline-flex items-center gap-1.5">
                        <Lock className="w-4 h-4" />
                        Terkunci — retur {detail.retur_disetujui_code || ""} sudah disetujui
                      </span>
                    ) : (
                      <>
                        {canEdit && (
                          <button onClick={() => del(detail.id)} data-testid="sale-detail-delete"
                            className="h-10 px-4 rounded-lg border border-rose-200 text-rose-600 hover:bg-rose-50 text-sm font-medium inline-flex items-center gap-1.5">
                            <Trash2 className="w-4 h-4" /> Hapus
                          </button>
                        )}
                        {/* F5: nota bertempo yang piutangnya SUDAH menerima uang tidak
                            bisa diedit — dijelaskan, bukan dibiarkan jadi tombol yang
                            ujungnya 409 (aturan 3 CLAUDE.md). Koreksinya lewat Edit
                            Pembayaran di menu Piutang Usaha (F8). */}
                        {canEdit && detail._tempo_terkunci_pembayaran && (
                          <span data-testid="sale-detail-locked-tempo"
                            title="Perbaiki lewat Edit Pembayaran di menu Finance → Piutang Usaha"
                            className="h-10 px-4 rounded-lg border border-amber-200 bg-amber-50 text-amber-700 text-sm font-medium inline-flex items-center gap-1.5">
                            <Lock className="w-4 h-4" />
                            Terkunci — piutangnya sudah dibayar sebagian
                          </span>
                        )}
                        {canEdit && detail._can_edit && (
                          <button onClick={() => openEdit(detail)} data-testid="sale-detail-edit"
                            className="h-10 px-4 rounded-lg bg-[#25D366] hover:bg-[#128C7E] text-white text-sm font-medium inline-flex items-center gap-1.5">
                            <Pencil className="w-4 h-4" /> Edit
                            {detail._boleh_edit_tempo && " Nota Bertempo"}
                          </button>
                        )}
                      </>
                    )}
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>
      )}

      {/* CREATE / EDIT MODAL */}
      {modal && (
        <div className="fixed inset-0 z-50 bg-black/50 backdrop-blur-sm grid place-items-center p-4 overflow-y-auto"
          onClick={saleGuard.requestClose}>
          <div ref={saleGuard.contentRef} className="bg-white rounded-2xl w-full max-w-2xl my-8" onClick={e => e.stopPropagation()}>
            <div className="sticky top-0 bg-[#25D366] text-white px-6 py-4 flex items-center justify-between rounded-t-2xl z-10">
              <div className="flex items-center gap-2">
                <MessageCircle className="w-5 h-5" />
                <h3 className="font-display font-semibold">{modalMode === "edit" ? "Edit Penjualan" : "Input Penjualan"}</h3>
              </div>
              <button onClick={saleGuard.requestClose} className="p-2 hover:bg-white/20 rounded-lg">
                <X className="w-4 h-4" />
              </button>
            </div>
            <div className="p-6 space-y-4">
              <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
                <F l="Tanggal"><DatePicker value={form.tanggal} onChange={v => setForm(s => ({ ...s, tanggal: v }))} data-testid="sale-tanggal" /></F>
                <F l="Waktu"><input type="time" value={form.waktu} onChange={e => setForm(s => ({ ...s, waktu: e.target.value }))} className={inp} data-testid="sale-waktu" /></F>
                <F l="Branch"><select value={form.branch} onChange={e => setForm(s => ({ ...s, branch: e.target.value }))} className={inp + " bg-white"}>
                  {BRANCHES.map(b => <option key={b} value={b}>{b || "— pilih —"}</option>)}
                </select></F>
                <F l="Nama Pelanggan *">
                  {/* B2 (brief 22 Sep): nota B2B/USER — nama WAJIB dari kartu
                      pelanggan (komponen yang SAMA dengan Input Nota). Nota umum
                      tetap ketik bebas. */}
                  {perluKartuPelanggan ? (
                    <PilihPelangganKartu
                      nama={form.nama_pelanggan} noHp={form.no_hp} kartu={form._kartu_pelanggan}
                      onUbahNama={v => setForm(s => ({ ...s, nama_pelanggan: v, _kartu_pelanggan: null }))}
                      onPilih={k => setForm(s => ({ ...s, nama_pelanggan: k.nama, no_hp: k.no_hp, _kartu_pelanggan: k }))}
                      className={inp} data-testid="sale-nama-pelanggan" />
                  ) : (
                    <input value={form.nama_pelanggan} onChange={e => setForm(s => ({ ...s, nama_pelanggan: e.target.value }))} className={inp} data-testid="sale-nama-pelanggan" />
                  )}
                </F>
                <F l="No HP"><input value={form.no_hp} onChange={e => setForm(s => ({ ...s, no_hp: e.target.value }))}
                  readOnly={perluKartuPelanggan && kartuCocok(form._kartu_pelanggan, form.nama_pelanggan, form.no_hp)}
                  className={inp} placeholder="08xxxx" data-testid="sale-no-hp" /></F>
                <F l="IMEI"><input value={form.imei} onChange={e => setForm(s => ({ ...s, imei: e.target.value }))} className={inp} placeholder="opsional" /></F>
                {/* Satu nota = satu gudang (kontrak G1 keputusan owner #4) —
                    hanya muncul kalau kasir berhak >1 gudang, lihat
                    docstring useGudangOptions. */}
                {tampilkanPilihGudang && (
                  <F l="Gudang *">
                    <select value={form.warehouse_id}
                      onChange={e => setForm(s => ({ ...s, warehouse_id: e.target.value }))}
                      data-testid="sale-warehouse" className={inp + " bg-white"}>
                      <option value="">— pilih gudang —</option>
                      {warehouses.map(w => <option key={w.id} value={w.id}>{w.nama}</option>)}
                    </select>
                  </F>
                )}
              </div>

              {/* F5 (brief owner 12 Sep 2026) — Jenis Pelanggan & Metode
                  Pembayaran/Tempo pada nota B2B/USER yang sudah melahirkan
                  piutang. Blok ini SENGAJA hanya muncul saat server bilang
                  boleh (`_boleh_edit_tempo`): menampilkannya pada nota tunai
                  biasa berarti kasir pemegang `sales.update` bisa menerbitkan
                  piutang lewat pintu belakang, tanpa satu approval pun. */}
              {modalMode === "edit" && form._boleh_edit_tempo && (
                <div className="rounded-xl border border-sky-200 bg-sky-50/60 p-3 space-y-3"
                     data-testid="sale-blok-tempo">
                  <p className="text-xs font-semibold uppercase tracking-wider text-sky-800">
                    Nota Bertempo — Piutang
                  </p>
                  <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
                    <F l="Jenis Pelanggan">
                      <select value={form.jenis_pelanggan || "umum"} data-testid="sale-jenis-pelanggan"
                        onChange={e => setForm(s => ({ ...s, jenis_pelanggan: e.target.value }))}
                        className="h-9 w-full px-3 rounded border border-slate-200 text-sm bg-white">
                        <option value="umum">Umum</option>
                        <option value="b2b">B2B</option>
                        <option value="user">USER</option>
                      </select>
                    </F>
                    <F l="Metode Pembayaran / Tempo">
                      <select value={form.tempo_pembayaran || ""} data-testid="sale-tempo"
                        onChange={e => setForm(s => ({ ...s, tempo_pembayaran: e.target.value }))}
                        className="h-9 w-full px-3 rounded border border-slate-200 text-sm bg-white">
                        <option value="">Tanpa tempo (bayar di tempat)</option>
                        <option value="net_3">Net 3 hari</option>
                        <option value="net_7">Net 7 hari</option>
                        <option value="net_10">Net 10 hari</option>
                        <option value="net_30">Net 30 hari</option>
                        <option value="net_90">Net 90 hari</option>
                        <option value="deposit">Deposit</option>
                      </select>
                    </F>
                    <F l="PIC Persetujuan Piutang">
                      <input value={form.pic_approval_piutang || ""} data-testid="sale-pic-piutang"
                        onChange={e => setForm(s => ({ ...s, pic_approval_piutang: e.target.value }))}
                        placeholder="wajib untuk jenis USER"
                        className="h-9 w-full px-3 rounded border border-slate-200 text-sm" />
                    </F>
                  </div>
                  <p className="text-[11px] text-sky-900">
                    Tanggal jatuh tempo dihitung ulang server dari tanggal nota + temponya, dan
                    dokumen piutangnya ikut disesuaikan begitu perubahan ini disimpan.
                  </p>
                </div>
              )}

              {/* Sumber Info / atribusi marketing (brief bos 11 Agu 2026) —
                  lihat components/SourceInfoField.jsx untuk alasan lengkap. */}
              <SourceInfoField
                sourceInfo={form.source_info}
                sourceRefId={form.source_ref_id}
                onChange={(v, refId) => setForm(s => ({ ...s, source_info: v, source_ref_id: refId }))}
                cfg={sourceInfoCfg}
                testidPrefix="sale-source-info"
              />

              <div>
                <div className="flex items-center justify-between mb-2">
                  <p className="text-xs font-semibold uppercase tracking-wider text-slate-600">Barang Dibeli</p>
                  <button onClick={addItem} data-testid="btn-add-item"
                    className="inline-flex items-center gap-1 text-xs font-medium text-emerald-700 hover:text-emerald-800">
                    <Plus className="w-3.5 h-3.5" /> Tambah Barang
                  </button>
                </div>
                <div className="space-y-2">
                  {form.items.map((it, i) => {
                    if (it.addition) return null;
                    // Paket/bundling (poin 8, brief bos 23 Agu 2026) — baris ini
                    // "paket" kalau SKU-nya adalah sku_bundle resep AKTIF (deteksi
                    // lewat KEBERADAAN resep, sama seperti `_tolak_stok_kosong` di
                    // backend — bukan lewat `it.komponen` terisi/tidak).
                    const resepBaris = it.sku ? resepBundlingMap[it.sku.trim().toUpperCase()] : null;
                    // F3 (keputusan 23 Sep, D6: aturan yang sama dengan Input Nota):
                    // harga baris PAKET terkunci = harga jual paketnya; potongan lewat
                    // kolom Diskon. Server menolak harga paket yang diketik ulang.
                    const hargaTerkunci = hargaPaketTerkunci(it, !!resepBaris);
                    return (
                    <div key={i} className="grid grid-cols-[1fr,120px,150px,auto] gap-2 items-start border border-slate-200 rounded-lg p-3">
                      <div>
                        {/* Kotak cari TUNGGAL milik EppleHub (kontrak G2) —
                            dipertahankan di atas fitur paket ZOA. */}
                        <ItemCariField index={i} item={it}
                          bolehLookupProduk={bolehLookupProduk} bolehLookupUnit={bolehLookupUnit}
                          warehouseId={form.warehouse_id} onWarehouseHint={onWarehouseHint}
                          resepBundlingMap={resepBundlingMap}
                          deriveKode={deriveKode}
                          /* Nama prop tetap `deriveKategori` (kontrak ItemCariField),
                              tapi fungsinya kini `deriveKategoriTeksBebas` dari lib/kategoriBarang
                              — sejak migrasi ZOA tahap 1 identifier lama tidak ada lagi, dan
                              merujuknya melempar ReferenceError saat baris item dirender. */
                          deriveKategori={deriveKategoriTeksBebas}
                          onPatch={(patch) => patchItem(i, patch)} />
                        {/* IMEI baris ini HANYA muncul untuk barang yang sudah tertaut SKU
                            (brief bos poin 4) — barang ketik bebas tanpa SKU tidak pernah
                            dipaksa mengisi IMEI, lihat docstring ItemImeiField. Baris PAKET
                            (poin 8) tidak pernah punya unit fisik untuk SKU paketnya sendiri
                            (lihat docstring bundling.py) — IMEI-nya per KOMPONEN, dipilih
                            lewat BundleKomponenPicker di bawah, bukan field ini. */}
                        {bolehLookupUnit && it.sku && !resepBaris && (
                          <ItemImeiField index={i} item={it} bolehLookup={bolehLookupUnit}
                            imeiTerpakaiLain={new Set(form.items
                              .filter((_, j) => j !== i)
                              .map(x => (x.imei || "").trim())
                              .filter(Boolean))}
                            onPatch={(patch) => patchItem(i, patch)} />
                        )}
                        {(resepBaris || (Array.isArray(it.komponen) && it.komponen.length > 0)) && (
                          <BundleKomponenPicker index={i} resep={resepBaris} komponen={it.komponen}
                            onChange={(next) => patchItem(i, { komponen: next })}
                            bolehLookupUnit={bolehLookupUnit}
                            imeiTerpakaiLain={kumpulkanSemuaImeiForm(form.items)}
                            produkInfoCache={produkInfoCache} ensureProdukInfo={ensureProdukInfo} />
                        )}
                      </div>
                      <div className="space-y-1">
                        <input type="number" placeholder="Harga" value={it.harga || ""}
                          onChange={e => updItem(i, "harga", e.target.value)}
                          disabled={hargaTerkunci}
                          data-testid={`sale-item-${i}-harga`}
                          className={`h-9 w-full px-3 rounded border border-slate-200 text-sm text-right font-mono ${
                            hargaTerkunci ? "bg-slate-50 text-slate-600" : ""}`} />
                        {hargaTerkunci && (
                          <p className="text-[10px] text-slate-500 inline-flex items-center gap-1" data-testid={`sale-item-${i}-harga-terkunci`}>
                            <Lock className="w-3 h-3" /> Harga paket terkunci
                          </p>
                        )}
                        {/* Diskon PRODUK per baris (brief 22 Sep D5/D6) — aturan yang
                            sama dengan Input Nota. Terkunci kalau nota ini masih
                            memakai diskon NOTA lama (campuran ditolak server). */}
                        <InputRupiah value={it.diskon} onChange={v => updItem(i, "diskon", v)}
                          placeholder="Diskon" testid={`sale-item-${i}-diskon`}
                          disabled={!!it.is_bonus || (diskonBaris === 0 && (form.diskon_nominal || 0) > 0)}
                          className={`h-8 w-full px-3 rounded border text-xs text-right font-mono ${
                            (Number(it.diskon) || 0) > (Number(it.harga) || 0) ? "border-rose-400 bg-rose-50" : "border-slate-200"}`} />
                        {/* Backend menolak harga Rp0 kecuali ditandai bonus — checkbox ini
                            BUKAN cara "mengakali" harga, hanya untuk barang yang memang
                            diberikan gratis (bonus/hadiah/garansi pembelian). */}
                        <label className="flex items-center gap-1.5 text-[10px] text-slate-500 cursor-pointer"
                          title="Centang kalau barang ini memang diberikan GRATIS (bonus/hadiah/garansi) — bukan cara mengosongkan harga jual.">
                          <input type="checkbox" checked={!!it.is_bonus}
                            onChange={e => updItem(i, "is_bonus", e.target.checked)}
                            data-testid={`sale-item-${i}-bonus`} className="w-3 h-3 accent-emerald-600" />
                          Bonus/Gratis
                        </label>
                      </div>
                      {/* Baris bertaut SKU: kategori diambil APA ADANYA dari master
                          produk (lihat ItemNamaField.pilih()) — dikunci, bukan
                          dropdown, supaya kasir tidak bisa diam-diam menimpanya
                          dengan kategori yang salah (akun neraca ikut salah kalau
                          itu terjadi, lihat memory/MIGRASI-KATEGORI-23AGU.md).
                          Lepas SKU dulu (tombol × di badge SKU) kalau memang perlu
                          diketik bebas. Baris TANPA SKU tetap dropdown biasa, karena
                          tidak ada sumber kebenaran lain selain pilihan kasir. */}
                      {it.sku ? (
                        <div data-testid={`item-kategori-${i}`}
                          title="Kategori mengikuti master produk — lepas tautan SKU untuk mengubah manual"
                          className="h-9 px-2 rounded border border-slate-200 bg-slate-50 text-xs text-slate-600 flex items-center">
                          {labelKategori(it.kategori)}
                        </div>
                      ) : (
                        <select value={normalKategori(it.kategori) || KATEGORI_DEFAULT} onChange={e => updItem(i, "kategori", e.target.value)}
                          data-testid={`item-kategori-${i}`}
                          className="h-9 px-2 rounded border border-slate-200 text-xs bg-white">
                          {KATEGORI_ITEM.map(k => <option key={k.v} value={k.v}>{k.l}</option>)}
                        </select>
                      )}
                      {form.items.length > 1 && (
                        <button onClick={() => rmItem(i)}
                          className="p-1.5 hover:bg-rose-50 rounded text-slate-400 hover:text-rose-600">
                          <Trash2 className="w-4 h-4" />
                        </button>
                      )}
                    </div>
                    );
                  })}
                </div>
                {can("inventory_produk", "view") && <SalesExtrasPicker form={form} setForm={setForm}/>}
                <div className="mt-2 flex justify-end text-sm">
                  <span className="text-slate-500 mr-2">Subtotal barang:</span>
                  <span className="font-mono font-bold text-slate-900">{idr(totalItems)}</span>
                </div>
                {diskonBaris > 0 && (
                  <div className="mt-2 grid grid-cols-1 sm:grid-cols-2 gap-3 items-end">
                    {/* SATU PIC per nota untuk seluruh diskon produk (D5). */}
                    <F l="PIC Approval Diskon *">
                      <input value={form.diskon_pic_approval || ""} data-testid="sale-diskon-pic"
                        onChange={e => setForm(s => ({ ...s, diskon_pic_approval: e.target.value }))}
                        placeholder="Siapa yang menyetujui diskon produk" className={inp} />
                    </F>
                    <p className="text-right text-xs text-rose-700">
                      Diskon produk: <span className="font-mono font-semibold">−{idr(diskonBaris)}</span>
                    </p>
                  </div>
                )}
              </div>

              {/* Diskon & Biaya Admin (brief bos) */}
              <div className="border border-slate-200 rounded-lg p-3 bg-slate-50/50 space-y-3">
                <div className="flex items-center justify-between">
                  <p className="text-xs font-semibold uppercase tracking-wider text-slate-600">Diskon &amp; Biaya Admin</p>
                  {/* PPN (keputusan owner 18 Agu 2026): CV ZOA berstatus PKP,
                      tarif 11%, harga barang di atas BELUM termasuk PPN. */}
                  <label className="inline-flex items-center gap-1.5 text-xs font-medium text-slate-700 cursor-pointer">
                    <input type="checkbox" checked={kenaPpn}
                      onChange={e => setForm(s => ({ ...s, kena_ppn: e.target.checked }))}
                      data-testid="sale-kena-ppn" className="w-3.5 h-3.5 accent-emerald-600" />
                    Kena PPN {PPN_PERSEN_PREVIEW}%
                  </label>
                </div>
                <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
                  <F l={diskonBaris > 0 ? "Diskon (jumlah diskon produk)"
                        : form.pembayaran_rinci
                          // Format baru tanpa diskon baris = diskon nota TERSIMPAN dari
                          // draf lama (dibawa apa adanya, tidak bisa diubah — D2/D6).
                          ? ((form.diskon_nominal || 0) > 0 ? "Diskon nota tersimpan (draf lama)" : "Diskon (jumlah diskon produk)")
                          : "Diskon nota (Rp)"}>
                    {/* D6: kalau ada diskon per baris — atau notanya format baru —
                        diskon nota = jumlah diskon produk, baca-saja. Diskon nota
                        lama (tanpa diskon baris) tetap bisa diubah seperti dulu. */}
                    <InputRupiah value={diskonBaris > 0 || form.pembayaran_rinci ? diskonNominal : form.diskon_nominal}
                      onChange={v => setForm(s => ({ ...s, diskon_nominal: v || 0 }))}
                      disabled={diskonBaris > 0 || !!form.pembayaran_rinci}
                      testid="sale-diskon" className={inp + " text-right font-mono"} />
                  </F>
                  {/* Biaya admin tidak dipungut lagi pada nota FORMAT BARU (D4) —
                      server menolaknya; nota lama tetap bisa diubah seperti dulu. */}
                  {!form.pembayaran_rinci && (<>
                  <F l="Biaya Admin">
                    <select value={form.biaya_admin_mode}
                      onChange={e => setForm(s => ({ ...s, biaya_admin_mode: e.target.value, biaya_admin_nilai: 0 }))}
                      data-testid="sale-admin-mode" className={inp + " bg-white"}>
                      <option value="">Tanpa biaya admin</option>
                      <option value="nominal">Nominal (Rp)</option>
                      <option value="persen">Persen (%)</option>
                    </select>
                  </F>
                  {form.biaya_admin_mode === "nominal" && (
                    <F l="Nilai Biaya Admin">
                      <InputRupiah value={form.biaya_admin_nilai}
                        onChange={v => setForm(s => ({ ...s, biaya_admin_nilai: v || 0 }))}
                        testid="sale-admin-nilai" className={inp + " text-right font-mono"} />
                    </F>
                  )}
                  {form.biaya_admin_mode === "persen" && (
                    <F l="Persentase Biaya Admin (%)">
                      <input type="number" min={0} max={100} value={form.biaya_admin_nilai || ""}
                        onChange={e => setForm(s => ({
                          ...s, biaya_admin_nilai: Math.min(100, Math.max(0, parseFloat(e.target.value) || 0)),
                        }))}
                        data-testid="sale-admin-nilai" className={inp + " text-right font-mono"} />
                    </F>
                  )}
                  </>)}
                </div>
                {/* Ringkasan hidup — SATU angka yang menjadi acuan validasi split payment.
                    Nota FORMAT BARU: angka hitungan SERVER (pratinjau), termasuk DP. */}
                {rinciEdit ? (
                <div className="bg-white border border-slate-200 rounded-lg p-3 text-xs space-y-1" data-testid="sale-ringkasan-rinci">
                  {!rincianRinci ? (
                    <p className="text-slate-500">{pratinjauRinci.gagal || pesanRinci[0] || "Menghitung tagihan…"}</p>
                  ) : (<>
                    <div className="flex justify-between text-slate-600"><span>Subtotal barang</span><span className="font-mono">{idr(rincianRinci.total_barang)}</span></div>
                    {rincianRinci.diskon > 0 && (
                      <div className="flex justify-between text-rose-700"><span>{diskonBaris > 0 ? "− Diskon produk" : "− Diskon nota"}</span><span className="font-mono">-{idr(rincianRinci.diskon)}</span></div>
                    )}
                    {rincianRinci.ppn_nominal > 0 && (
                      <div className="flex justify-between text-emerald-700"><span>+ PPN ({rincianRinci.ppn_persen}%)</span><span className="font-mono">+{idr(rincianRinci.ppn_nominal)}</span></div>
                    )}
                    <div className="flex justify-between font-semibold text-slate-900"><span>Nilai nota</span><span className="font-mono">{idr(rincianRinci.tagihan)}</span></div>
                    {rincianRinci.trade_in > 0 && (
                      <div className="flex justify-between text-violet-700"><span>− Trade-in</span><span className="font-mono">-{idr(rincianRinci.trade_in)}</span></div>
                    )}
                    {rincianRinci.dp > 0 && (
                      <div className="flex justify-between text-emerald-700"><span>− Downpayment (DP)</span><span className="font-mono">-{idr(rincianRinci.dp)}</span></div>
                    )}
                    <div className="flex justify-between font-bold text-slate-900 pt-1 border-t border-slate-100">
                      <span>Total harus dibayar</span>
                      <span className="font-mono" data-testid="sale-total-harus-dibayar">{idr(rincianRinci.harus_dibayar)}</span>
                    </div>
                  </>)}
                </div>
                ) : (
                <div className="bg-white border border-slate-200 rounded-lg p-3 text-xs space-y-1">
                  <div className="flex justify-between text-slate-600">
                    <span>Subtotal barang</span><span className="font-mono">{idr(totalItems)}</span>
                  </div>
                  {diskonNominal > 0 && (
                    <div className="flex justify-between text-rose-700">
                      <span>− Diskon</span><span className="font-mono">-{idr(diskonNominal)}</span>
                    </div>
                  )}
                  {biayaAdminRp > 0 && (
                    <div className="flex justify-between text-emerald-700">
                      <span>+ Biaya Admin{biayaAdminMode === "persen" ? ` (${biayaAdminNilai || 0}%)` : ""}</span>
                      <span className="font-mono">+{idr(biayaAdminRp)}</span>
                    </div>
                  )}
                  {kenaPpn && (
                    <div className="flex justify-between text-emerald-700" data-testid="sale-ringkasan-ppn">
                      <span>+ PPN ({PPN_PERSEN_PREVIEW}%)</span>
                      <span className="font-mono">+{idr(ppnPreview)}</span>
                    </div>
                  )}
                  {tradeInTotal > 0 && (
                    <div className="flex justify-between text-violet-700">
                      <span>− Trade-in</span><span className="font-mono">-{idr(tradeInTotal)}</span>
                    </div>
                  )}
                  <div className="flex justify-between font-bold text-slate-900 pt-1 border-t border-slate-100">
                    <span>Total harus dibayar</span>
                    <span className="font-mono" data-testid="sale-total-harus-dibayar">{idr(totalHarusDibayar)}</span>
                  </div>
                </div>
                )}
              </div>

              <div className="border border-slate-200 rounded-lg p-3 bg-slate-50/50">
                <div className="flex items-center justify-between mb-2">
                  <p className="text-xs font-semibold uppercase tracking-wider text-slate-600 flex items-center gap-1.5">
                    <CreditCard className="w-3.5 h-3.5" /> Metode Pembayaran
                  </p>
                  {/* Nota FORMAT BARU selalu berbentuk baris (D4) — toggle Split
                      tidak ditawarkan: kembali ke "satu metode tanpa nominal"
                      ditolak server. */}
                  {!form.pembayaran_rinci && (
                  <label className="inline-flex items-center gap-2 text-xs cursor-pointer">
                    <input type="checkbox" checked={form.is_split}
                      onChange={e => setForm(s => ({
                        ...s,
                        is_split: e.target.checked,
                        payments: e.target.checked && (!s.payments || s.payments.length === 0)
                          ? [{ method: s.metode_pembayaran || "Cash", amount: totalHarusDibayar, keterangan: "" }]
                          : s.payments,
                      }))}
                      data-testid="sale-split-toggle" />
                    <Split className="w-3.5 h-3.5" /> Split Payment
                  </label>
                  )}
                </div>

                {!form.is_split ? (
                  <PaymentMethodSelect
                    value={form.metode_pembayaran}
                    onChange={v => setForm(s => ({ ...s, metode_pembayaran: v }))}
                    groups={paymentGroups}
                    className={inp + " bg-white"} testid="sale-metode" />
                ) : (
                  <div className="space-y-2">
                    {(form.payments || []).map((p, i) => (
                      <div key={i} className="grid grid-cols-[1fr,120px,auto] sm:grid-cols-[1fr,180px,auto] gap-2 items-center">
                        <PaymentMethodSelect
                          value={p.method}
                          onChange={v => updPay(i, "method", v)}
                          groups={form.pembayaran_rinci ? paymentGroupsRinci : paymentGroups}
                          testid={`sale-pay-${i}-method`}
                          className="h-9 px-3 rounded border border-slate-200 text-sm bg-white" />
                        <input type="number" placeholder="Nominal" value={p.amount || ""}
                          onChange={e => updPay(i, "amount", e.target.value)}
                          data-testid={`sale-pay-${i}-amount`}
                          className="h-9 px-3 rounded border border-slate-200 text-sm text-right font-mono" />
                        {(form.payments || []).length > 1 ? (
                          <button onClick={() => rmPay(i)}
                            className="p-1.5 hover:bg-rose-50 rounded text-slate-400 hover:text-rose-600">
                            <Trash2 className="w-4 h-4" />
                          </button>
                        ) : <span />}
                        {/* Keterangan / No. Kontrak (D3) — ikut tersimpan & tercetak,
                            tidak mengubah jurnal. Dimuat dari dokumen supaya edit
                            tidak menghapusnya. */}
                        <input value={p.keterangan || ""} maxLength={120}
                          onChange={e => updPay(i, "keterangan", e.target.value)}
                          placeholder="Keterangan / No. Kontrak (opsional)"
                          data-testid={`sale-pay-${i}-keterangan`}
                          className="col-span-3 h-8 px-2 rounded border border-slate-200 text-xs" />
                      </div>
                    ))}
                    <div className="flex items-center justify-between pt-1">
                      <button onClick={addPay} data-testid="btn-add-pay"
                        className="inline-flex items-center gap-1 text-xs font-medium text-emerald-700 hover:text-emerald-800">
                        <Plus className="w-3.5 h-3.5" /> Tambah Metode
                      </button>
                      <div className="text-xs text-right">
                        <p className="text-slate-500">Total pembayaran: <span className="font-mono font-semibold text-slate-900">{idr(totalSplit)}</span></p>
                        {form.pembayaran_rinci && modalMode === "edit" ? (
                          // Format baru: status dari pratinjau SERVER (bertempo boleh kurang).
                          <p className={rinciSegar && pratinjauRinci.data.cocok ? "text-emerald-700" : "text-amber-700"}
                            data-testid="sale-status-bayar">
                            {!rinciSegar ? "Menghitung…"
                              : pratinjauRinci.data.cocok
                                ? ((pratinjauRinci.data.sisa_piutang || 0) > 0
                                  ? `✓ Sisa ${idr(pratinjauRinci.data.sisa_piutang)} jadi piutang`
                                  : "✓ Sesuai yang harus dibayar")
                                : (pesanRinci[0] || "Belum sesuai")}
                          </p>
                        ) : (
                          <p className={Math.abs(splitBalance) < 0.5 ? "text-emerald-700" : "text-amber-700"}>
                            {Math.abs(splitBalance) < 0.5 ? "✓ Sesuai harga terima" : `Selisih ${idr(splitBalance)}`}
                          </p>
                        )}
                      </div>
                    </div>
                  </div>
                )}
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                {/* "Harga Terima" bukan uang pada nota FORMAT BARU (D6) — uangnya
                    baris pembayaran; kolomnya hanya untuk nota format lama. */}
                {!form.pembayaran_rinci ? (
                <F l="Harga Terima">
                  <input type="number" value={form.harga_terima || ""}
                    onChange={e => setForm(s => ({ ...s, harga_terima: parseFloat(e.target.value) || 0 }))}
                    className={inp + " text-right font-mono"} placeholder={String(totalItems)}
                    data-testid="sale-harga-terima" />
                </F>
                ) : <div />}
                <F l="Trade-in?">
                  <div className="flex items-center gap-4 h-10">
                    <label className="inline-flex items-center gap-2 text-sm cursor-pointer">
                      <input type="checkbox" checked={form.trade_in}
                        onChange={e => setForm(s => ({
                          ...s, trade_in: e.target.checked,
                          // Centang pertama langsung menyiapkan satu baris unit supaya
                          // kasir tidak menghadapi area kosong tanpa petunjuk.
                          trade_in_items: e.target.checked && !(s.trade_in_items || []).length
                            ? [{ nama: "", harga: 0, imei: "" }] : s.trade_in_items,
                        }))}
                        data-testid="sale-tradein" /> Ya
                    </label>
                    {form.trade_in && (
                      <input value={form.trade_in_note}
                        onChange={e => setForm(s => ({ ...s, trade_in_note: e.target.value }))}
                        placeholder="catatan trade-in (opsional)"
                        className="flex-1 h-8 px-2 rounded border border-slate-200 text-xs" />
                    )}
                  </div>
                </F>
                {/* Unit trade-in: dulu halaman ini HANYA punya kotak catatan, jadi
                    nota trade-in yang dibuat lewat Input Nota tidak bisa diedit di
                    sini tanpa kehilangan unitnya. Form-nya disamakan dengan
                    InputNota — nama, IMEI, dan nilai per unit. */}
                {form.trade_in && (
                  <div className="md:col-span-2 border border-violet-200 bg-violet-50/40 rounded-lg p-3 space-y-2">
                    <div className="flex items-center justify-between">
                      <p className="text-xs font-semibold text-violet-800">Unit yang diterima (trade-in)</p>
                      <p className="text-xs text-violet-800">Total: <b>{idr(tradeInTotal)}</b></p>
                    </div>
                    {(form.trade_in_items || []).map((t, i) => (
                      <div key={i} className="grid grid-cols-12 gap-2">
                        <input value={t.nama} data-testid={`tradein-name-${i}`}
                          onChange={e => setForm(s => {
                            const a = [...(s.trade_in_items || [])]; a[i] = { ...a[i], nama: e.target.value };
                            return { ...s, trade_in_items: a };
                          })}
                          placeholder="Nama unit (mis. iPhone 12 128GB)"
                          className="col-span-5 h-9 px-2 rounded border border-slate-200 text-sm" />
                        <input value={t.imei} data-testid={`tradein-imei-${i}`}
                          onChange={e => setForm(s => {
                            const a = [...(s.trade_in_items || [])]; a[i] = { ...a[i], imei: e.target.value };
                            return { ...s, trade_in_items: a };
                          })}
                          placeholder="IMEI / SN"
                          className="col-span-4 h-9 px-2 rounded border border-slate-200 text-sm font-mono" />
                        <input type="number" value={t.harga || ""} data-testid={`tradein-price-${i}`}
                          onChange={e => setForm(s => {
                            const a = [...(s.trade_in_items || [])]; a[i] = { ...a[i], harga: parseFloat(e.target.value) || 0 };
                            return { ...s, trade_in_items: a };
                          })}
                          placeholder="Nilai"
                          className="col-span-2 h-9 px-2 rounded border border-slate-200 text-sm" />
                        <button type="button" onClick={() => setForm(s => ({
                          ...s, trade_in_items: (s.trade_in_items || []).filter((_, x) => x !== i),
                        }))} className="col-span-1 h-9 rounded border border-slate-200 text-slate-400 hover:text-rose-600 hover:bg-rose-50 text-sm">×</button>
                        {/* Kolom wajib trade-in (brief 29 Agu poin 4) — daftar yang SAMA
                            dengan Input Nota (lib/notaPenjualan). Server menolak unit
                            tanpa kolom ini pada nota format baru, jadi kasir harus
                            bisa mengisinya di sini, bukan menabrak 400 buntu. */}
                        <select value={t.status || ""} data-testid={`tradein-status-${i}`}
                          onChange={e => setForm(s => {
                            const a = [...(s.trade_in_items || [])]; a[i] = { ...a[i], status: e.target.value };
                            return { ...s, trade_in_items: a };
                          })}
                          className="col-span-4 h-8 px-2 rounded border border-slate-200 text-xs bg-white">
                          <option value="">Status *</option>
                          {t.status && !TI_STATUS.includes(t.status) && <option value={t.status}>{t.status}</option>}
                          {TI_STATUS.map(v => <option key={v} value={v}>{v}</option>)}
                        </select>
                        <input value={t.warna || ""} data-testid={`tradein-warna-${i}`}
                          onChange={e => setForm(s => {
                            const a = [...(s.trade_in_items || [])]; a[i] = { ...a[i], warna: e.target.value };
                            return { ...s, trade_in_items: a };
                          })}
                          placeholder="Warna *"
                          className="col-span-4 h-8 px-2 rounded border border-slate-200 text-xs" />
                        <select value={t.kelengkapan || ""} data-testid={`tradein-kelengkapan-${i}`}
                          onChange={e => setForm(s => {
                            const a = [...(s.trade_in_items || [])]; a[i] = { ...a[i], kelengkapan: e.target.value };
                            return { ...s, trade_in_items: a };
                          })}
                          className="col-span-4 h-8 px-2 rounded border border-slate-200 text-xs bg-white">
                          <option value="">Kelengkapan *</option>
                          {t.kelengkapan && !TI_KELENGKAPAN.includes(t.kelengkapan) && <option value={t.kelengkapan}>{t.kelengkapan}</option>}
                          {TI_KELENGKAPAN.map(v => <option key={v} value={v}>{v}</option>)}
                        </select>
                      </div>
                    ))}
                    <button type="button" data-testid="tradein-add"
                      onClick={() => setForm(s => ({
                        ...s, trade_in_items: [...(s.trade_in_items || []),
                          { nama: "", harga: 0, imei: "", status: "", warna: "", kelengkapan: "" }],
                      }))}
                      className="h-8 px-3 rounded-lg border border-violet-300 bg-white hover:bg-violet-50 text-xs font-medium text-violet-800">
                      + Tambah unit trade-in
                    </button>
                  </div>
                )}
                <F l="Nama Penjual *">
                  <select value={form.nama_penjual}
                    onChange={e => setForm(s => ({ ...s, nama_penjual: e.target.value }))}
                    className={inp + " bg-white"} data-testid="sale-penjual">
                    <option value="">— pilih karyawan —</option>
                    {branchEmployees.map(e => (
                      <option key={e.id} value={e.nama}>
                        {e.nama}{e.branch ? ` · ${e.branch}` : ""}
                      </option>
                    ))}
                  </select>
                </F>
                <F l="Catatan">
                  <input value={form.catatan} onChange={e => setForm(s => ({ ...s, catatan: e.target.value }))}
                    className={inp} placeholder="opsional" />
                </F>
              </div>

              {/* Paket/bundling (poin 8-9, brief bos 23 Agu 2026) — alasan tombol
                  Simpan tertahan, aturan #3 CLAUDE.md: kasir harus tahu KENAPA
                  sebelum menekan, bukan menabrak toast 400 sesudahnya. */}
              {bundleAlasanTertahan && (
                <p className="text-xs text-amber-700 bg-amber-50 border border-amber-200 rounded-lg px-3 py-2"
                  data-testid="sale-bundle-alasan-tertahan">
                  {bundleAlasanTertahan}
                </p>
              )}
              <div className="flex justify-end gap-2 pt-2 border-t border-slate-100">
                <button onClick={saleGuard.requestClose}
                  className="h-10 px-4 rounded-lg border border-slate-200 text-sm">Batal</button>
                <button onClick={save} data-testid="btn-save-sale" disabled={saving || !!bundleAlasanTertahan}
                  title={bundleAlasanTertahan || undefined}
                  className="h-10 px-5 rounded-lg bg-[#25D366] hover:bg-[#128C7E] disabled:opacity-50 text-white text-sm font-medium inline-flex items-center gap-2">
                  {modalMode === "edit" ? <Pencil className="w-4 h-4" /> : <Send className="w-4 h-4" />}
                  {saving ? "Menyimpan..." : (modalMode === "edit" ? "Simpan Perubahan" : "Kirim")}
                </button>
              </div>
            </div>
            {saleGuard.confirming && (
              <UnsavedGuardBar onStay={saleGuard.cancelClose} onDiscard={saleGuard.confirmDiscard} testid="sale-modal-guard-bar" />
            )}
          </div>
        </div>
      )}

      {/* DRAFT WA AI MODAL */}
      {draftModal && (
        <div className="fixed inset-0 z-[60] bg-black/60 backdrop-blur-sm grid place-items-center p-4"
          onClick={() => setDraftModal(null)}>
          <div className="bg-white rounded-2xl w-full max-w-lg" onClick={e => e.stopPropagation()}
            data-testid="draft-wa-modal">
            <div className="bg-gradient-to-r from-emerald-600 to-emerald-500 text-white px-5 py-3.5 flex items-center justify-between rounded-t-2xl">
              <div className="flex items-center gap-2">
                <Sparkles className="w-5 h-5" />
                <div>
                  <h3 className="font-display font-semibold text-sm">Draft Balasan WA — AI</h3>
                  <p className="text-[11px] text-emerald-50/90">Untuk: {draftModal.nama_pelanggan || "Pelanggan"} {draftModal.no_hp && `· ${draftModal.no_hp}`}</p>
                </div>
              </div>
              <button onClick={() => setDraftModal(null)}
                className="p-1.5 hover:bg-white/20 rounded-lg" data-testid="draft-wa-close">
                <X className="w-4 h-4" />
              </button>
            </div>
            <div className="p-5 space-y-3">
              {draftModal.loading ? (
                <div className="min-h-[120px] flex flex-col items-center justify-center gap-2 text-sm text-slate-500">
                  <Sparkles className="w-6 h-6 text-emerald-500 animate-pulse" />
                  <span>GPT-5.2 sedang menyusun pesan...</span>
                </div>
              ) : (
                <>
                  <textarea
                    value={draftModal.message}
                    onChange={e => setDraftModal(m => ({ ...m, message: e.target.value }))}
                    rows={6}
                    data-testid="draft-wa-textarea"
                    className="w-full border border-slate-200 rounded-lg p-3 text-sm resize-none focus:outline-none focus:ring-2 focus:ring-emerald-500/40 whitespace-pre-wrap"
                  />
                  <p className="text-[11px] text-slate-500">
                    <Info className="w-3 h-3 inline mb-0.5 mr-1" />
                    Tone: kasual chat teman. Bebas diedit sebelum dikirim.
                  </p>
                </>
              )}
            </div>
            <div className="border-t border-slate-100 px-5 py-3 flex flex-wrap gap-2 justify-between bg-slate-50 rounded-b-2xl">
              <button onClick={regenDraft} disabled={draftModal.loading} data-testid="draft-wa-regen"
                className="inline-flex items-center gap-1.5 h-9 px-3 rounded-lg border border-slate-200 bg-white hover:bg-slate-100 text-slate-700 text-xs font-medium disabled:opacity-50">
                <Sparkles className="w-3.5 h-3.5" /> Susun Ulang
              </button>
              <div className="flex gap-2">
                <button onClick={copyDraft} disabled={draftModal.loading || !draftModal.message} data-testid="draft-wa-copy"
                  className="inline-flex items-center gap-1.5 h-9 px-3 rounded-lg border border-slate-200 bg-white hover:bg-slate-100 text-slate-700 text-xs font-medium disabled:opacity-50">
                  <Copy className="w-3.5 h-3.5" /> Salin
                </button>
                {draftModal.no_hp ? (
                  <a
                    href={`https://wa.me/${(draftModal.no_hp.replace(/\D/g, "").startsWith("0") ? "62" + draftModal.no_hp.replace(/\D/g, "").slice(1) : draftModal.no_hp.replace(/\D/g, ""))}?text=${encodeURIComponent(draftModal.message || "")}`}
                    target="_blank" rel="noopener noreferrer"
                    data-testid="draft-wa-send"
                    className={`inline-flex items-center gap-1.5 h-9 px-3 rounded-lg bg-[#25D366] hover:bg-[#128C7E] text-white text-xs font-medium ${draftModal.loading ? "pointer-events-none opacity-50" : ""}`}>
                    <Send className="w-3.5 h-3.5" /> Buka WhatsApp
                  </a>
                ) : (
                  <span className="text-[11px] text-slate-400 self-center italic">No HP kosong — hanya bisa salin</span>
                )}
              </div>
            </div>
          </div>
        </div>
      )}

      {/* CETAK FAKTUR / STRUK MODAL — alur: pilih jenis (tombol Cetak Faktur/
          Cetak Struk) sudah menentukan `cetakModal.jenis`; kalau jenis itu
          punya lebih dari satu template, langkah "pilih_template" muncul dulu
          sebelum pratinjau dicetak. */}
      {cetakModal && (
        <div className="fixed inset-0 z-[60] bg-black/60 backdrop-blur-sm grid place-items-center p-4 overflow-y-auto"
          onClick={() => setCetakModal(null)}>
          <div className="bg-white rounded-2xl w-full max-w-3xl my-8 shadow-2xl" onClick={e => e.stopPropagation()}
            data-testid="cetak-faktur-modal">
            <div className="sticky top-0 bg-slate-900 text-white px-6 py-4 flex items-center justify-between rounded-t-2xl z-10">
              <h3 className="font-display font-semibold inline-flex items-center gap-2">
                <Printer className="w-4 h-4" /> Cetak {cetakModal.jenis === "struk" ? "Struk" : "Faktur"}
              </h3>
              <button onClick={() => setCetakModal(null)} className="p-2 hover:bg-white/20 rounded-lg">
                <X className="w-4 h-4" />
              </button>
            </div>
            <div className="p-6">
              {cetakModal.step === "pilih_template" ? (
                <div className="space-y-4">
                  <PemilihTemplate templates={cetakModal.templates} value={cetakModal.templateId}
                    onChange={(id) => setCetakModal(m => ({ ...m, templateId: id }))}
                    jenisLabel={cetakModal.jenis === "struk" ? "struk" : "faktur"} testidPrefix="cetak-tpl" />
                  <div className="flex justify-end">
                    <button onClick={() => muatDokumenCetak(cetakModal.jenis, cetakModal.templates.find(t => t.id === cetakModal.templateId))}
                      data-testid="cetak-tpl-lanjut"
                      className="h-10 px-4 rounded-lg bg-emerald-600 hover:bg-emerald-700 text-white text-sm font-medium">
                      Lanjutkan
                    </button>
                  </div>
                </div>
              ) : cetakModal.step === "kosong" ? (
                <p className="text-sm text-slate-500 text-center py-8" data-testid="cetak-tpl-kosong">
                  Belum ada template {cetakModal.jenis === "struk" ? "struk" : "faktur"}. Buat dulu di menu
                  Template Faktur &amp; Struk.
                </p>
              ) : cetakModal.step === "tanpa_izin" ? (
                <p className="text-sm text-slate-500 text-center py-8" data-testid="cetak-tpl-tanpa-izin">
                  Akun Anda belum punya izin melihat Template Faktur &amp; Struk, jadi format struk tidak bisa
                  dipilih dari sini. Minta admin mencentang modul &quot;Faktur &amp; Struk&quot; pada role Anda.
                </p>
              ) : cetakModal.loading || !cetakModal.data ? (
                <LoadingState loading jumlahKartu={1} data-testid="cetak-faktur-loading" />
              ) : (
                <InvoicePreview data={cetakModal.data} />
              )}
            </div>
          </div>
        </div>
      )}

      {/* Dialog konfirmasi PPN sebelum simpan (brief bos) */}
      {ppnConfirm && (
        <div className="fixed inset-0 z-[68] bg-black/60 grid place-items-center p-4" onClick={() => setPpnConfirm(null)}>
          <div className="bg-white rounded-2xl w-full max-w-md shadow-2xl" onClick={e => e.stopPropagation()}
            data-testid="ppn-confirm-modal">
            <div className="bg-emerald-600 text-white px-5 py-3 rounded-t-2xl flex items-center justify-between">
              <h3 className="font-semibold text-sm">Konfirmasi PPN {PPN_PERSEN_PREVIEW}%</h3>
              <button onClick={() => setPpnConfirm(null)} className="p-1.5 hover:bg-white/20 rounded"><X className="w-4 h-4" /></button>
            </div>
            <div className="p-5 space-y-3">
              <p className="text-xs text-slate-600">
                Transaksi ini akan dicatat KENA PPN. PPN akan dipisah dari pendapatan dan
                dicatat sebagai kewajiban pajak (PPN Keluaran) yang wajib disetor —
                pastikan sudah benar sebelum melanjutkan.
              </p>
              <div className="rounded-lg bg-slate-50 border border-slate-200 px-3 py-2 text-xs space-y-0.5">
                <div className="flex justify-between text-slate-600">
                  <span>DPP (sebelum PPN)</span>
                  <span className="font-mono">{idr(totalItems)}</span>
                </div>
                <div className="flex justify-between text-slate-600">
                  <span>PPN ({PPN_PERSEN_PREVIEW}%)</span>
                  <span className="font-mono">{idr(ppnPreview)}</span>
                </div>
                <div className="flex justify-between font-semibold text-slate-900 pt-0.5 border-t border-slate-200">
                  <span>Total harus dibayar</span>
                  <span className="font-mono">{idr(totalHarusDibayar)}</span>
                </div>
              </div>
            </div>
            <div className="border-t border-slate-100 p-4 flex justify-end gap-2">
              <button onClick={() => setPpnConfirm(null)} data-testid="btn-ppn-kembali"
                className="h-10 px-4 rounded-lg border border-slate-200 text-sm">
                Kembali
              </button>
              <button onClick={() => doSaveSale(ppnConfirm)} disabled={saving} data-testid="btn-ppn-lanjutkan"
                className="h-10 px-5 rounded-lg bg-emerald-600 hover:bg-emerald-700 text-white text-sm font-medium disabled:opacity-50">
                {saving ? "Menyimpan..." : "Lanjutkan"}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* PIN PERSETUJUAN HAPUS (brief #23) */}
      {pinDialog && (
        <div className="fixed inset-0 z-[70] bg-black/60 backdrop-blur-sm grid place-items-center p-4"
          onClick={() => setPinDialog(null)}>
          <div className="bg-white rounded-2xl w-full max-w-sm shadow-2xl" onClick={e => e.stopPropagation()}
            data-testid="pin-dialog">
            <div className="bg-rose-600 text-white px-5 py-3 rounded-t-2xl flex items-center justify-between">
              <h3 className="font-semibold text-sm inline-flex items-center gap-1.5">
                <Lock className="w-4 h-4" /> PIN Persetujuan Hapus
              </h3>
              <button onClick={() => setPinDialog(null)} className="p-1.5 hover:bg-white/20 rounded">
                <X className="w-4 h-4" />
              </button>
            </div>
            <div className="p-5 space-y-3">
              <p className="text-xs text-slate-600">
                Penghapusan butuh persetujuan. Minta PIN ke pemegang izin (lihat Pusat Approval), lalu masukkan di sini.
              </p>
              <input value={pinDialog.pin}
                onChange={e => setPinDialog(d => ({ ...d, pin: e.target.value.replace(/\D/g, "").slice(0, 6), error: "" }))}
                maxLength={6} inputMode="numeric" placeholder="6 digit PIN" autoFocus
                data-testid="pin-input"
                className="w-full h-11 px-3 rounded-lg border border-slate-200 text-center text-lg tracking-[0.3em] font-mono" />
              {pinDialog.error && <p className="text-xs text-rose-600">{pinDialog.error}</p>}
            </div>
            <div className="border-t border-slate-100 p-4 flex justify-end gap-2">
              <button onClick={() => setPinDialog(null)} className="h-10 px-4 rounded-lg border border-slate-200 text-sm">
                Batal
              </button>
              <button onClick={submitPinDialog} disabled={pinDialog.submitting || pinDialog.pin.length !== 6}
                data-testid="pin-kirim"
                className="h-10 px-5 rounded-lg bg-rose-600 hover:bg-rose-700 disabled:opacity-50 text-white text-sm font-medium">
                {pinDialog.submitting ? "Memproses..." : "Kirim & Hapus"}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* AJUKAN RETUR PENJUALAN (brief bos) — hanya mengirim pengajuan;
          approval + jurnal/stok terjadi di halaman /retur. */}
      {returModal && (
        <div className="fixed inset-0 z-[65] bg-black/60 backdrop-blur-sm grid place-items-center p-4 overflow-y-auto"
          onClick={returGuard.requestClose}>
          <div ref={returGuard.contentRef} className="bg-white rounded-2xl w-full max-w-xl my-8 shadow-2xl" onClick={e => e.stopPropagation()}
            data-testid="retur-ajukan-modal">
            <div className="sticky top-0 bg-rose-600 text-white px-6 py-4 flex items-center justify-between rounded-t-2xl z-10">
              <h3 className="font-display font-semibold inline-flex items-center gap-2">
                <Undo2 className="w-5 h-5" /> Ajukan Retur Penjualan
              </h3>
              <button onClick={returGuard.requestClose} className="p-2 hover:bg-white/20 rounded-lg" data-testid="retur-ajukan-close">
                <X className="w-4 h-4" />
              </button>
            </div>
            <div className="p-6 space-y-4">
              {returModal.loading ? (
                <LoadingState loading jumlahKartu={1} data-testid="retur-ajukan-loading" />
              ) : (
                <>
                  <div className="space-y-2">
                    <p className="text-xs font-semibold uppercase tracking-wider text-slate-600">Pilih Barang &amp; Alasan</p>
                    {returModal.items.length === 0 ? (
                      <p className="text-sm text-slate-400 italic">Tidak ada barang di transaksi ini.</p>
                    ) : returModal.items.map((r, i) => (
                      <div key={i}
                        className={`border rounded-lg p-3 ${r.sisa <= 0 ? "border-slate-100 bg-slate-50 opacity-60" : "border-slate-200"}`}>
                        <label className="flex items-center gap-2 cursor-pointer">
                          <input type="checkbox" checked={r.checked} disabled={r.sisa <= 0}
                            onChange={e => toggleReturItem(i, e.target.checked)}
                            data-testid={`retur-ajukan-cek-${i}`} />
                          <span className="flex-1 text-sm font-medium text-slate-900">{r.nama_barang}</span>
                          {r.isBundleComponent && (
                            <span className="text-[10px] px-1.5 py-0.5 rounded bg-slate-100 text-slate-600 shrink-0">
                              isi paket
                            </span>
                          )}
                          <span className="text-xs font-mono text-slate-500">{idr(r.harga)}</span>
                        </label>
                        {r.komponen && r.komponen.length > 0 && (
                          <div className="mt-2 ml-6 rounded border border-amber-200 bg-amber-50 p-2"
                            data-testid={`retur-ajukan-paket-isi-${i}`}>
                            <p className="text-[11px] font-semibold text-amber-900">
                              Ini baris paket — mencentangnya hanya mengembalikan uangnya.
                            </p>
                            <p className="text-[11px] text-amber-800 mt-0.5">
                              Barangnya tidak ikut kembali ke stok. Supaya barangnya benar-benar
                              kembali, centang juga baris isinya di bawah:
                            </p>
                            <ul className="mt-1 space-y-0.5">
                              {r.komponen.map((k, ki) => (
                                <li key={ki} className="text-[11px] text-amber-900 font-mono">
                                  • {k.nama || k.sku}{k.qty ? ` ×${k.qty}` : ""}
                                </li>
                              ))}
                            </ul>
                          </div>
                        )}
                        {r.sisa <= 0 ? (
                          <p className="text-[11px] text-slate-400 mt-1 ml-6">Sudah diretur semua ({r.sudah}/{r.qtyAsal})</p>
                        ) : r.checked && (
                          <div className="mt-2 ml-6 grid grid-cols-[110px,1fr] gap-2 items-start">
                            <label className="text-[11px] text-slate-500">
                              Qty (maks {r.sisa})
                              <input type="number" min={1} max={r.sisa} value={r.qtyRetur}
                                onChange={e => setReturQty(i, e.target.value)}
                                data-testid={`retur-ajukan-qty-${i}`}
                                className="mt-0.5 h-8 w-full px-2 rounded border border-slate-200 text-sm" />
                            </label>
                            <label className="text-[11px] text-slate-500">
                              Alasan
                              <input value={r.alasan} onChange={e => setReturAlasan(i, e.target.value)}
                                placeholder="mis. barang cacat / salah pesan"
                                data-testid={`retur-ajukan-alasan-${i}`}
                                className="mt-0.5 h-8 w-full px-2 rounded border border-slate-200 text-sm" />
                            </label>
                          </div>
                        )}
                      </div>
                    ))}
                  </div>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                    <F l="Nominal Kembali">
                      <InputRupiah value={returModal.nominal_kembali} testid="retur-ajukan-nominal"
                        onChange={v => setReturModal(m => ({ ...m, nominal_kembali: v || 0, nominalTouched: true }))}
                        className={inp + " text-right font-mono"} />
                      {returModal.diskonNominal > 0 && (
                        <p className="text-[11px] text-slate-500" data-testid="retur-ajukan-nominal-hint">
                          {returModal.diskonPerBaris
                            ? "Memakai harga bersih per barang (sesudah diskon produk)"
                            : "Otomatis dipotong diskon proporsional"}
                        </p>
                      )}
                      {/* Nota ber-PPN (brief 18 Agu 2026): pelanggan membayar DPP+PPN, jadi
                          batas & saran refund SUDAH mencakup PPN — tanpa hint ini kasir
                          mengira batasnya berhenti di harga barang saja. */}
                      {returModal.kenaPpn && (
                        <p className="text-[11px] text-emerald-700" data-testid="retur-ajukan-ppn-hint">
                          Termasuk PPN {idr(returModal.ppnNominalSale)} — bisa dikembalikan
                          sampai {idr(returModal.sisaNilaiMaks)} (DPP + PPN)
                        </p>
                      )}
                    </F>
                    <F l="Bentuk Pengembalian">
                      {/* Permintaan owner 26 Agu 2026: cash / transfer / TUKAR UNIT.
                          Cash & transfer sama-sama "uang keluar" — bedanya cuma akun
                          mana, jadi keduanya satu pilihan di sini dan akunnya dipilih
                          di bawah. Tukar unit sama sekali bukan uang keluar. */}
                      <select value={returModal.jenisKembali || "uang"}
                        onChange={e => setReturModal(m => ({ ...m, jenisKembali: e.target.value }))}
                        data-testid="retur-ajukan-jenis" className={inp + " bg-white"}>
                        <option value="uang">Uang kembali (cash / transfer)</option>
                        <option value="tukar_unit">Tukar unit — tahan jadi kredit pelanggan</option>
                      </select>
                      {returModal.jenisKembali === "tukar_unit" && (
                        <p className="text-[11px] text-violet-700 mt-1" data-testid="retur-ajukan-tukar-hint">
                          Tidak ada uang keluar hari ini. Nilainya jadi <b>kredit milik
                          pelanggan</b> dan bisa dipakai sebagai pembayaran di nota
                          berikutnya (metode pembayaran &quot;Kredit Retur&quot;) — hanya
                          oleh pelanggan dengan nama DAN No HP yang sama.
                        </p>
                      )}
                    </F>
                    {/* I1 (brief 22 Sep): nota tanpa No HP -> nomor pemilik kredit
                        wajib diisi di sini, dan approver melihatnya sebelum setuju. */}
                    {returModal.jenisKembali === "tukar_unit" && (
                      <F l={returModal.saleNoHp ? "No HP pemilik kredit" : "No HP pelanggan (wajib — nota tanpa No HP)"}>
                        {returModal.saleNoHp ? (
                          <p className={inp + " bg-slate-50 flex items-center font-mono"} data-testid="retur-ajukan-hp-nota">
                            {returModal.saleNoHp}
                          </p>
                        ) : (
                          <input value={returModal.noHpKredit} inputMode="tel" placeholder="08xxxxxxxxxx"
                            onChange={e => setReturModal(m => ({ ...m, noHpKredit: e.target.value }))}
                            data-testid="retur-ajukan-hp-kredit" className={inp} />
                        )}
                      </F>
                    )}
                    {(returModal.jenisKembali || "uang") !== "tukar_unit" && (
                      <F l="Akun Pengembalian">
                        <select value={returModal.akun} onChange={e => setReturModal(m => ({ ...m, akun: e.target.value }))}
                          data-testid="retur-ajukan-akun" className={inp + " bg-white"}>
                          <option value="">— pilih akun kas/bank —</option>
                          {kasBankAccounts.map(a => (
                            <option key={a.id} value={a.id}>{a.name} ({idr(a.balance)})</option>
                          ))}
                        </select>
                      </F>
                    )}
                  </div>
                  <F l="Catatan (opsional)">
                    <input value={returModal.catatan} onChange={e => setReturModal(m => ({ ...m, catatan: e.target.value }))}
                      data-testid="retur-ajukan-catatan" className={inp} />
                  </F>
                </>
              )}
            </div>
            <div className="border-t border-slate-100 px-6 py-4 flex justify-end gap-2">
              <button onClick={returGuard.requestClose} className="h-10 px-4 rounded-lg border border-slate-200 text-sm">
                Batal
              </button>
              <button onClick={submitRetur} disabled={returModal.loading || returModal.submitting}
                data-testid="retur-ajukan-submit"
                className="h-10 px-5 rounded-lg bg-rose-600 hover:bg-rose-700 disabled:opacity-50 text-white text-sm font-medium inline-flex items-center gap-2">
                <Undo2 className="w-4 h-4" /> {returModal.submitting ? "Mengirim..." : "Ajukan Retur"}
              </button>
            </div>
            {returGuard.confirming && (
              <UnsavedGuardBar onStay={returGuard.cancelClose} onDiscard={returGuard.confirmDiscard} testid="retur-ajukan-guard-bar" />
            )}
          </div>
        </div>
      )}
    </div>
  );
}

// Autocomplete nama barang -> master produk (opsional, gerbang izin
// inventory_produk). Barang di luar katalog (jasa, titipan, barang lama)
// TETAP bisa diketik bebas — SKU tidak pernah dipaksa. Kalau lookup gagal
// atau 403, otomatis diam-diam nonaktif tanpa toast merah yang menghalangi.
function ItemNamaField({ index, item, bolehLookup, onPatch, resepBundlingMap }) {
  const [saran, setSaran] = useState([]);
  const debounceRef = useRef(null);
  const reqSeq = useRef(0);

  useEffect(() => () => { if (debounceRef.current) clearTimeout(debounceRef.current); }, []);

  const cari = (teks) => {
    // Kategori di sini murni SARAN cadangan (baris masih ketik bebas, belum
    // tertaut SKU) — begitu `item.sku` sudah terisi (lihat `pilih()` di bawah),
    // baris kategori berubah jadi badge terkunci di JSX dan tebakan ini tidak
    // lagi relevan sampai kasir melepas tautan SKU-nya sendiri.
    onPatch({ nama_barang: teks, kode: deriveKode(teks), kategori: deriveKategoriTeksBebas(teks) });
    if (!bolehLookup) return;
    if (debounceRef.current) clearTimeout(debounceRef.current);
    const q = teks.trim();
    if (q.length < 2) { setSaran([]); return; }
    debounceRef.current = setTimeout(async () => {
      const seq = ++reqSeq.current;
      try {
        const { data } = await api.get("/inventory/products/lookup", { params: { q } });
        if (seq === reqSeq.current) setSaran(data.items || []);
      } catch {
        // Diam-diam nonaktif — modul lookup mungkin tidak diizinkan atau error jaringan.
        if (seq === reqSeq.current) setSaran([]);
      }
    }, 300);
  };

  const pilih = (p) => {
    // Paket/bundling (poin 8, brief bos 23 Agu 2026) — kalau produk yang
    // dipilih adalah sku_bundle resep aktif, bangun scaffold komponen kosong
    // SEKALIGUS di sini. WAJIB direset di SETIAP pemilihan (bukan hanya kalau
    // def ada) — kalau tidak, baris yang tadinya paket lalu diganti ke barang
    // biasa akan membawa `komponen` basi dari produk sebelumnya.
    const def = resepBundlingMap?.[(p.sku || "").trim().toUpperCase()];
    onPatch({
      nama_barang: p.nama,
      sku: p.sku,
      harga: p.harga_jual || 0,
      kode: deriveKode(p.nama),
      // Kategori diambil APA ADANYA dari master produk — TIDAK ditebak dari
      // nama. `kategori` wajib diisi saat produk dibuat (products.py), jadi
      // `p.kategori` semestinya selalu ada; "" hanya jaring pengaman kalau
      // data lama entah bagaimana kosong (dropdown akan kosong, memaksa
      // kasir memilih manual, alih-alih diam-diam menebak salah).
      kategori: p.kategori || "",
      imei: "",
      komponen: def ? bangunKomponenKosong(def) : null,
    });
    setSaran([]);
  };

  return (
    <div className="relative">
      <input placeholder="Nama Barang" value={item.nama_barang}
        onChange={e => cari(e.target.value)}
        data-testid={`item-cari-${index}`}
        className="h-9 px-3 rounded border border-slate-200 text-sm w-full" />
      {item.sku && (
        <span data-testid={`item-sku-badge-${index}`}
          className="mt-1 inline-flex items-center gap-1 text-[10px] font-mono bg-emerald-50 text-emerald-700 border border-emerald-200 rounded px-1.5 py-0.5">
          SKU {item.sku}
          {/* Melepas SKU ikut melepas IMEI + rincian komponen paket yang sudah
              dipilih — keduanya cuma sah selama masih tertaut katalog
              (pemilihnya hilang begitu sku dikosongkan), jadi menyisakannya
              jadi nilai basi yang tidak bisa diedit lagi lewat UI. */}
          <button type="button" onClick={() => onPatch({ sku: "", imei: "", komponen: null })}
            title="Lepas tautan katalog — kembali ke ketik bebas"
            className="text-emerald-600 hover:text-emerald-900 leading-none">×</button>
        </span>
      )}
      {saran.length > 0 && (
        <div className="absolute z-10 left-0 right-0 mt-1 bg-white border border-slate-200 rounded-lg shadow-lg max-h-52 overflow-y-auto">
          {/* Stok habis TIDAK bisa dipilih (keputusan bos). Ditegakkan juga di
              backend supaya tidak bisa ditembus; di sini sekadar mencegah
              kasir terlanjur memilih lalu ditolak saat menyimpan. Barang di
              luar katalog tetap bisa diketik bebas — itu jalan keluarnya.

              Paket (poin 8-11): SKU paket TIDAK PERNAH punya stok fisik
              sendiri (`p.stock_qty` selalu 0, lihat docstring bundling.py) —
              memakai `stock_qty` mentah di sini akan membuat SEMUA paket
              tampak permanen "stok habis" dan tidak bisa dipilih sama sekali.
              Untuk SKU yang cocok resep aktif, "stok" yang dipakai untuk
              keputusan pilih/tidak adalah STOK VIRTUAL (MIN komponen). */}
          {saran.map((p, n) => {
            const defPaket = resepBundlingMap?.[(p.sku || "").trim().toUpperCase()];
            const stokEfektif = defPaket ? (defPaket.stok_virtual?.qty ?? 0) : (p.stock_qty || 0);
            const habis = !(stokEfektif > 0);
            return (
              <button key={p.sku} type="button" disabled={habis}
                onClick={() => { if (!habis) pilih(p); }}
                title={habis ? "Stok habis — terima barangnya dulu lewat Pembelian & Terima" : ""}
                data-testid={`item-saran-${index}-${n}`}
                className={`w-full text-left px-3 py-2 text-xs border-b border-slate-50 last:border-0 ${
                  habis ? "bg-slate-50 opacity-60 cursor-not-allowed" : "hover:bg-emerald-50"}`}>
                <span className="font-medium text-slate-900">{p.nama}</span>
                <span className="font-mono text-[10px] text-slate-400 ml-1.5">{p.sku}</span>
                <span className="text-[10px] text-slate-500 ml-1.5">{idr(p.harga_jual || 0)}</span>
                {defPaket && (
                  <span className="text-[10px] ml-1.5 font-medium text-emerald-700">PAKET</span>
                )}
                <span className={`text-[10px] ml-1.5 font-medium ${habis ? "text-rose-600" : "text-emerald-600"}`}>
                  {habis ? "stok habis — tidak bisa dijual" : `stok ${stokEfektif}`}
                </span>
              </button>
            );
          })}
        </div>
      )}
    </div>
  );
}

// ==================== Pemilih IMEI per baris -> unit inventory ====================
// Brief bos poin 4: IMEI HANYA boleh DIPILIH dari unit yang terdaftar &
// berstatus tersedia di inventory — tidak lagi diketik bebas. Beda dari
// ItemCariField (components/pos/ItemCariField.jsx, kontrak G2 bagian C):
// mengetik di sini TIDAK PERNAH langsung mengisi `item.imei` (itu yang
// membuatnya "ketik bebas"), field baru terisi begitu kasir mengklik salah
// satu SARAN hasil pencarian. Sekali terisi, tampil sebagai badge (mirip
// badge katalog) — untuk mengubahnya, lepas dulu lewat "×". Field ini HANYA
// dirender untuk baris yang produknya belum langsung membawa unit spesifik
// (dipilih sebagai `tipe:"produk"` di ItemCariField, bukan `tipe:"unit"`
// yang sudah mengisi IMEI-nya sekaligus).
//
// CATATAN BACKEND (baca sebelum mengandalkan ini sebagai satu-satunya
// pagar): validasi `_tolak_stok_kosong` (routers/sales.py, TIDAK disentuh
// dari sisi frontend ini) menolak IMEI yang tidak terdaftar/tidak
// "tersedia" per BARIS, tapi TIDAK mengunci/mereservasi unit — dua nota
// terpisah yang kebetulan memilih IMEI yang sama di baris yang berbeda
// keduanya akan lolos validasi server (unit tidak pernah ditandai "terjual"
// oleh sales.py, lihat routers/retur.py "BATASAN YANG SENGAJA DIPUTUSKAN").
// Yang bisa dicegah dari sini HANYA duplikat DALAM SATU transaksi yang
// sedang diisi (lewat `imeiTerpakaiLain`) — bukan lintas nota/transaksi.
function ItemImeiField({ index, item, bolehLookup, imeiTerpakaiLain, onPatch }) {
  const [cari, setCari] = useState("");
  const [saran, setSaran] = useState([]);
  const [mencari, setMencari] = useState(false);
  const debounceRef = useRef(null);
  const reqSeq = useRef(0);

  useEffect(() => () => { if (debounceRef.current) clearTimeout(debounceRef.current); }, []);

  const cariUnit = (teks) => {
    setCari(teks);
    if (!bolehLookup) return;
    if (debounceRef.current) clearTimeout(debounceRef.current);
    const q = teks.trim();
    if (q.length < 3) { setSaran([]); setMencari(false); return; }
    debounceRef.current = setTimeout(async () => {
      const seq = ++reqSeq.current;
      setMencari(true);
      try {
        const { data } = await api.get("/inventory/units/pilihan-jual", { params: { q, sku: item.sku } });
        if (seq === reqSeq.current) setSaran(data.items || []);
      } catch {
        // Diam-diam nonaktif — modul lookup mungkin tidak diizinkan atau error jaringan.
        if (seq === reqSeq.current) setSaran([]);
      } finally {
        if (seq === reqSeq.current) setMencari(false);
      }
    }, 300);
  };

  const pilih = (u) => {
    // G1 (keputusan 23 Sep): unit second yang punya Harga Jual Unit sendiri
    // mengisi harga baris dengan harga itu (kasir tetap bisa mengubahnya).
    // Tanpa medan itu dari server = perilaku lama persis (harga tidak disentuh).
    // Baris PAKET tidak pernah sampai ke sini (pemilih ini tidak dirender untuk
    // paket), jadi harga paket yang terkunci (F3) tidak tertimpa harga unit.
    onPatch({ imei: u.identifier, ...(u.harga_unit_khusus ? { harga: u.harga_bawaan } : {}) });
    setCari("");
    setSaran([]);
  };

  if (item.imei) {
    return (
      <div className="mt-1">
        <span data-testid={`item-imei-badge-${index}`}
          className="inline-flex items-center gap-1 text-[10px] font-mono bg-violet-50 text-violet-700 border border-violet-200 rounded px-1.5 py-0.5">
          IMEI {item.imei}
          <button type="button" onClick={() => onPatch({ imei: "" })}
            title="Lepas pilihan IMEI"
            className="text-violet-600 hover:text-violet-900 leading-none">×</button>
        </span>
      </div>
    );
  }

  return (
    <div className="relative mt-1">
      <input value={cari} onChange={e => cariUnit(e.target.value)}
        placeholder="Cari IMEI/SN dari stok (opsional)…" data-testid={`item-imei-cari-${index}`}
        className="h-8 px-2 rounded border border-slate-200 text-[11px] w-full font-mono" />
      {saran.length > 0 && (
        <div className="absolute z-10 left-0 right-0 mt-1 bg-white border border-slate-200 rounded-lg shadow-lg max-h-40 overflow-y-auto">
          {saran.map((u, n) => {
            const dipakai = imeiTerpakaiLain?.has(u.identifier);
            return (
              <button key={u.id} type="button" disabled={dipakai}
                onClick={() => { if (!dipakai) pilih(u); }}
                title={dipakai ? "Unit ini sudah dipilih di baris lain pada nota ini" : ""}
                data-testid={`item-imei-saran-${index}-${n}`}
                className={`w-full text-left px-2 py-1.5 text-[11px] border-b border-slate-50 last:border-0 ${
                  dipakai ? "bg-slate-50 opacity-60 cursor-not-allowed" : "hover:bg-violet-50"}`}>
                <span className="font-mono font-medium text-slate-900">{u.identifier}</span>
                {u.warehouse_nama && <span className="text-slate-400 ml-1.5">· {u.warehouse_nama}</span>}
                {dipakai && <span className="text-rose-600 ml-1.5">sudah dipilih di baris lain</span>}
              </button>
            );
          })}
        </div>
      )}
      {cari.trim().length >= 3 && !mencari && saran.length === 0 && (
        <p className="text-[10px] text-slate-400 mt-0.5">Tidak ada unit tersedia yang cocok.</p>
      )}
    </div>
  );
}

const inp = "h-10 px-3 rounded-lg border border-slate-200 text-sm w-full focus:outline-none focus:ring-2 focus:ring-emerald-600/40";
function F({ l, children }) {
  return <label className="flex flex-col gap-1"><span className="text-xs font-medium text-slate-600">{l}</span>{children}</label>;
}
function Field({ label, children }) {
  return (
    <div>
      <p className="text-xs text-slate-500 uppercase tracking-wider mb-0.5">{label}</p>
      <div className="text-sm text-slate-900">{children}</div>
    </div>
  );
}
